import { useState, useEffect, useRef } from 'react';
import {
  BookOpen,
  Users,
  MapPin,
  Box,
  BarChart2,
  FileText,
  Activity,
  Sun,
  Moon,
  Scissors,
  ChevronRight,
  ChevronDown,
  Upload,
  AlertCircle,
  Loader2,
  Zap,
  Target,
  Layers,
  GraduationCap,
  Sparkles,
  AlertTriangle,
  CheckCircle,
  Eye,
  EyeOff,
  Download,
  X,
  Info,
  AlertOctagon,
  TrendingUp,
  Clock,
  MessageCircle,
  FileEdit
} from 'lucide-react';
import ScriptEditor from './components/editor/ScriptEditor';
import FrameworkTabs from './components/analysis/FrameworkTabs';
import RewriteModal from './components/analysis/RewriteModal';
import FullRewriteModal from './components/analysis/FullRewriteModal';
import { requestFrameworkAnalysis } from './api/aiAnalysis';
import { requestBeatRewrite, requestFullRewrite } from './api/rewriteHelpers';

// --- Helper Components ---

const Card = ({ children, className = "" }) => (
  <div className={`bg-white rounded-xl shadow-sm border border-slate-200 ${className}`}>
    {children}
  </div>
);

const Badge = ({ children, type = "neutral" }) => {
  const styles = {
    neutral: "bg-slate-100 text-slate-700",
    primary: "bg-blue-100 text-blue-700",
    success: "bg-emerald-100 text-emerald-700",
    warning: "bg-amber-100 text-amber-700",
    danger: "bg-rose-100 text-rose-700",
    purple: "bg-purple-100 text-purple-700",
    indigo: "bg-indigo-100 text-indigo-700",
    dark: "bg-slate-800 text-white",
  };
  return (
    <span className={`px-2.5 py-0.5 rounded-full text-xs font-medium ${styles[type] || styles.neutral}`}>
      {children}
    </span>
  );
};

const ProfessorBubble = ({ name, role, text, type = "critique", fix = null }) => {
    const isMcKee = name === "The Professor";
    const isBlake = name === "The Cat Saver";

    return (
        <div className="flex items-start space-x-4 p-4 bg-slate-50 rounded-lg border border-slate-200">
            <div className={`flex-shrink-0 w-12 h-12 rounded-full flex items-center justify-center text-xl shadow-sm ${isMcKee ? 'bg-slate-800 text-white' : isBlake ? 'bg-amber-500 text-white' : 'bg-indigo-600 text-white'}`}>
                {isMcKee ? <GraduationCap size={24}/> : isBlake ? <Zap size={24}/> : <Layers size={24}/>}
            </div>
            <div className="flex-1">
                <div className="flex items-baseline space-x-2 mb-1">
                    <h4 className="font-bold text-slate-900">{name}</h4>
                    <span className="text-xs text-slate-500 uppercase tracking-wide">{role}</span>
                </div>
                <div className={`text-sm leading-relaxed ${type === 'praise' ? 'text-slate-700' : 'text-slate-600'}`}>
                    {text}
                </div>
                {fix && (
                    <div className="mt-3 p-3 bg-white border border-yellow-200 rounded-md">
                        <div className="flex items-center text-yellow-700 text-xs font-bold uppercase mb-1">
                            <Sparkles size={12} className="mr-1"/> Suggested Fix
                        </div>
                        <p className="text-xs text-slate-600">{fix}</p>
                    </div>
                )}
            </div>
        </div>
    );
};

// --- Details Modal Component ---
const DetailsModal = ({ isOpen, onClose, title, content, feedback }) => {
    if (!isOpen) return null;

    return (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4 backdrop-blur-sm" onClick={onClose}>
            <div className="bg-white rounded-xl shadow-2xl w-full max-w-2xl max-h-[85vh] overflow-hidden flex flex-col" onClick={e => e.stopPropagation()}>
                <div className="p-4 border-b border-slate-100 flex justify-between items-center bg-slate-50">
                    <h3 className="font-bold text-lg text-slate-800">{title}</h3>
                    <button onClick={onClose} className="text-slate-400 hover:text-slate-600 transition-colors">
                        <X size={20} />
                    </button>
                </div>
                <div className="p-6 overflow-y-auto">
                    {/* Script Content */}
                    <div className="mb-6">
                        <h4 className="text-xs font-bold text-slate-400 uppercase mb-2 flex items-center">
                            <FileText size={14} className="mr-1"/> Script Text
                        </h4>
                        <div className="bg-slate-50 p-4 rounded-lg border border-slate-200 font-mono text-sm text-slate-700 whitespace-pre-wrap max-h-64 overflow-y-auto shadow-inner">
                            {content ? content : <span className="italic text-slate-400">No text content available for this section.</span>}
                        </div>
                    </div>

                    {/* Feedback Section */}
                    <div>
                        <h4 className="text-xs font-bold text-slate-400 uppercase mb-2 flex items-center">
                            <GraduationCap size={14} className="mr-1"/> Targeted Feedback
                        </h4>
                        {feedback && feedback.length > 0 ? (
                            <div className="space-y-3">
                                {feedback.map((item, idx) => (
                                    <div key={idx} className={`p-3 rounded-lg border text-sm ${item.type === 'positive' ? 'bg-green-50 border-green-100 text-green-800' : 'bg-amber-50 border-amber-100 text-amber-800'}`}>
                                        <div className="font-semibold mb-1 flex items-center">
                                            {item.type === 'positive' ? <CheckCircle size={14} className="mr-1"/> : <AlertTriangle size={14} className="mr-1"/>}
                                            {item.title}
                                        </div>
                                        <p>{item.text}</p>
                                    </div>
                                ))}
                            </div>
                        ) : (
                            <div className="text-sm text-slate-500 italic">No specific issues detected in this section. Good work!</div>
                        )}
                    </div>
                </div>
                <div className="p-4 border-t border-slate-100 bg-slate-50 flex justify-end">
                    <button onClick={onClose} className="px-4 py-2 bg-slate-800 text-white text-sm font-medium rounded-lg hover:bg-slate-700 transition-colors">
                        Close
                    </button>
                </div>
            </div>
        </div>
    );
};

// --- Character Timeline Component ---

const CharacterTimeline = ({ characters, totalScenes, onSceneClick }) => {
    const [hoveredChar, setHoveredChar] = useState(null);
    const [hoveredScene, setHoveredScene] = useState(null);
    const [selectedCharacters, setSelectedCharacters] = useState(new Set());
    const [showGapsOnly, setShowGapsOnly] = useState(false);

    // Get top 12 characters by line count for the visualization
    const displayChars = characters.slice(0, 12);

    // Calculate presence percentage for each character
    const getPresencePercent = (char) => {
        return Math.round((char.sceneCount / totalScenes) * 100);
    };

    // Determine if character has concerning gaps
    const hasLongGap = (char) => char.gaps && char.gaps.some(g => g.size > 10);

    // Toggle character selection for comparison
    const toggleCharacter = (charName) => {
        const newSet = new Set(selectedCharacters);
        if (newSet.has(charName)) {
            newSet.delete(charName);
        } else {
            newSet.add(charName);
        }
        setSelectedCharacters(newSet);
    };

    // Color palette for characters
    const charColors = [
        'bg-blue-500', 'bg-emerald-500', 'bg-purple-500', 'bg-rose-500',
        'bg-amber-500', 'bg-cyan-500', 'bg-indigo-500', 'bg-pink-500',
        'bg-teal-500', 'bg-orange-500', 'bg-lime-500', 'bg-fuchsia-500'
    ];

    return (
        <div className="bg-white rounded-xl shadow-sm border border-slate-200 overflow-hidden">
            {/* Header */}
            <div className="p-4 border-b border-slate-100 bg-slate-50">
                <div className="flex justify-between items-center">
                    <div>
                        <h3 className="font-bold text-slate-800 flex items-center">
                            <TrendingUp size={18} className="mr-2 text-indigo-500" />
                            Character Presence Timeline
                        </h3>
                        <p className="text-xs text-slate-500 mt-1">
                            Visual map of when each character appears across {totalScenes} scenes
                        </p>
                    </div>
                    <div className="flex items-center space-x-3">
                        <label className="flex items-center text-xs text-slate-600 cursor-pointer">
                            <input
                                type="checkbox"
                                checked={showGapsOnly}
                                onChange={(e) => setShowGapsOnly(e.target.checked)}
                                className="mr-2 rounded border-slate-300"
                            />
                            Highlight gaps only
                        </label>
                    </div>
                </div>
            </div>

            {/* Legend */}
            <div className="px-4 py-3 border-b border-slate-100 bg-slate-50/50">
                <div className="flex flex-wrap gap-3 text-xs">
                    <div className="flex items-center">
                        <div className="w-4 h-3 bg-blue-500 rounded-sm mr-1.5"></div>
                        <span className="text-slate-600">Scene appearance</span>
                    </div>
                    <div className="flex items-center">
                        <div className="w-4 h-3 bg-red-200 border border-red-300 rounded-sm mr-1.5"></div>
                        <span className="text-slate-600">Long gap (10+ scenes)</span>
                    </div>
                    <div className="flex items-center">
                        <div className="w-4 h-3 bg-amber-400 rounded-sm mr-1.5"></div>
                        <span className="text-slate-600">First appearance</span>
                    </div>
                    <div className="flex items-center">
                        <div className="w-4 h-3 bg-slate-400 rounded-sm mr-1.5"></div>
                        <span className="text-slate-600">Last appearance</span>
                    </div>
                </div>
            </div>

            {/* Timeline Grid */}
            <div className="p-4 overflow-x-auto">
                <div className="min-w-[600px]">
                    {/* Scene numbers header */}
                    <div className="flex mb-2">
                        <div className="w-32 flex-shrink-0"></div>
                        <div className="flex-1 flex">
                            {Array.from({ length: totalScenes }, (_, i) => i + 1).map(sceneNum => (
                                <div
                                    key={sceneNum}
                                    className={`flex-1 text-center text-[10px] font-mono cursor-pointer transition-colors ${
                                        hoveredScene === sceneNum ? 'text-indigo-600 font-bold' : 'text-slate-400'
                                    } ${sceneNum % 5 === 0 ? 'text-slate-600' : ''}`}
                                    onMouseEnter={() => setHoveredScene(sceneNum)}
                                    onMouseLeave={() => setHoveredScene(null)}
                                    onClick={() => onSceneClick && onSceneClick(sceneNum)}
                                    title={`Scene ${sceneNum}`}
                                >
                                    {sceneNum % 5 === 0 ? sceneNum : '·'}
                                </div>
                            ))}
                        </div>
                        <div className="w-16 flex-shrink-0 text-right text-xs text-slate-400 font-medium">
                            Presence
                        </div>
                    </div>

                    {/* Character rows */}
                    {displayChars.map((char, charIdx) => {
                        const isSelected = selectedCharacters.has(char.name);
                        const isHovered = hoveredChar === char.name;
                        const color = charColors[charIdx % charColors.length];
                        const presencePct = getPresencePercent(char);

                        return (
                            <div
                                key={char.name}
                                className={`flex items-center py-1.5 border-b border-slate-50 transition-all ${
                                    isHovered ? 'bg-indigo-50' : ''
                                } ${isSelected ? 'bg-blue-50' : ''}`}
                                onMouseEnter={() => setHoveredChar(char.name)}
                                onMouseLeave={() => setHoveredChar(null)}
                            >
                                {/* Character name */}
                                <div
                                    className="w-32 flex-shrink-0 pr-3 cursor-pointer"
                                    onClick={() => toggleCharacter(char.name)}
                                >
                                    <div className="flex items-center">
                                        <div className={`w-2 h-2 rounded-full ${color} mr-2 flex-shrink-0`}></div>
                                        <span className={`text-sm font-medium truncate ${
                                            hasLongGap(char) ? 'text-amber-700' : 'text-slate-700'
                                        }`} title={char.name}>
                                            {char.name.length > 12 ? char.name.substring(0, 12) + '...' : char.name}
                                        </span>
                                    </div>
                                    <div className="text-[10px] text-slate-400 ml-4">
                                        {char.sceneCount} scenes, {char.lines} lines
                                    </div>
                                </div>

                                {/* Timeline cells */}
                                <div className="flex-1 flex h-6">
                                    {Array.from({ length: totalScenes }, (_, i) => i + 1).map(sceneNum => {
                                        const isPresent = char.sceneAppearances.includes(sceneNum);
                                        const isFirst = sceneNum === char.firstAppearance;
                                        const isLast = sceneNum === char.lastAppearance;
                                        const isInGap = char.gaps.some(g => sceneNum > g.after && sceneNum < g.before);

                                        // Determine cell styling
                                        let cellClass = 'flex-1 mx-px rounded-sm transition-all ';

                                        if (isPresent) {
                                            if (isFirst) {
                                                cellClass += 'bg-amber-400';
                                            } else if (isLast) {
                                                cellClass += 'bg-slate-400';
                                            } else {
                                                cellClass += color;
                                            }
                                            cellClass += ' hover:ring-2 hover:ring-indigo-400 hover:ring-offset-1 cursor-pointer';
                                        } else if (showGapsOnly && isInGap) {
                                            cellClass += 'bg-red-100 border border-red-200';
                                        } else if (!showGapsOnly && sceneNum > char.firstAppearance && sceneNum < char.lastAppearance) {
                                            cellClass += isInGap ? 'bg-red-50 border border-dashed border-red-200' : 'bg-slate-50';
                                        } else {
                                            cellClass += 'bg-transparent';
                                        }

                                        // Highlight column on hover
                                        if (hoveredScene === sceneNum) {
                                            cellClass += ' ring-1 ring-indigo-300';
                                        }

                                        return (
                                            <div
                                                key={sceneNum}
                                                className={cellClass}
                                                onClick={() => isPresent && onSceneClick && onSceneClick(sceneNum)}
                                                title={isPresent ? `${char.name} in Scene ${sceneNum}` :
                                                       isInGap ? `Gap: ${char.name} missing` : ''}
                                            />
                                        );
                                    })}
                                </div>

                                {/* Presence percentage */}
                                <div className="w-16 flex-shrink-0 text-right">
                                    <span className={`text-xs font-bold ${
                                        presencePct >= 50 ? 'text-green-600' :
                                        presencePct >= 25 ? 'text-blue-600' :
                                        presencePct >= 10 ? 'text-amber-600' :
                                        'text-slate-400'
                                    }`}>
                                        {presencePct}%
                                    </span>
                                </div>
                            </div>
                        );
                    })}

                    {/* Act markers */}
                    <div className="flex mt-3 pt-3 border-t border-slate-200">
                        <div className="w-32 flex-shrink-0 text-xs text-slate-400 font-medium">Story Structure</div>
                        <div className="flex-1 flex relative h-6">
                            {/* Act 1 */}
                            <div
                                className="absolute left-0 h-full bg-blue-100 rounded-l flex items-center justify-center"
                                style={{ width: '25%' }}
                            >
                                <span className="text-[10px] font-bold text-blue-600">ACT 1</span>
                            </div>
                            {/* Act 2 */}
                            <div
                                className="absolute h-full bg-amber-100 flex items-center justify-center"
                                style={{ left: '25%', width: '50%' }}
                            >
                                <span className="text-[10px] font-bold text-amber-600">ACT 2</span>
                            </div>
                            {/* Act 3 */}
                            <div
                                className="absolute right-0 h-full bg-rose-100 rounded-r flex items-center justify-center"
                                style={{ width: '25%' }}
                            >
                                <span className="text-[10px] font-bold text-rose-600">ACT 3</span>
                            </div>
                        </div>
                        <div className="w-16 flex-shrink-0"></div>
                    </div>
                </div>
            </div>

            {/* Gap Warnings */}
            {displayChars.some(c => hasLongGap(c)) && (
                <div className="p-4 bg-amber-50 border-t border-amber-100">
                    <h4 className="text-sm font-bold text-amber-800 flex items-center mb-2">
                        <AlertTriangle size={14} className="mr-1.5" />
                        Character Continuity Warnings
                    </h4>
                    <div className="space-y-1">
                        {displayChars.filter(c => hasLongGap(c)).map(char => (
                            <div key={char.name} className="text-xs text-amber-700">
                                <span className="font-medium">{char.name}</span>:
                                {char.gaps.filter(g => g.size > 10).map((gap, i) => (
                                    <span key={i}>
                                        {i > 0 ? ', ' : ' '}
                                        missing for {gap.size} scenes (after scene {gap.after})
                                    </span>
                                ))}
                            </div>
                        ))}
                    </div>
                </div>
            )}

            {/* Insights Summary */}
            <div className="p-4 bg-slate-50 border-t border-slate-200">
                <h4 className="text-sm font-bold text-slate-700 mb-3">Quick Insights</h4>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                    <div className="bg-white p-3 rounded-lg border border-slate-200">
                        <div className="text-2xl font-bold text-indigo-600">
                            {displayChars[0]?.name || 'N/A'}
                        </div>
                        <div className="text-xs text-slate-500">Protagonist (most lines)</div>
                    </div>
                    <div className="bg-white p-3 rounded-lg border border-slate-200">
                        <div className="text-2xl font-bold text-emerald-600">
                            {getPresencePercent(displayChars[0])}%
                        </div>
                        <div className="text-xs text-slate-500">Protagonist presence</div>
                    </div>
                    <div className="bg-white p-3 rounded-lg border border-slate-200">
                        <div className="text-2xl font-bold text-amber-600">
                            {displayChars.filter(c => c.firstAppearance > totalScenes * 0.25).length}
                        </div>
                        <div className="text-xs text-slate-500">Late introductions (after Act 1)</div>
                    </div>
                    <div className="bg-white p-3 rounded-lg border border-slate-200">
                        <div className="text-2xl font-bold text-rose-600">
                            {displayChars.filter(c => hasLongGap(c)).length}
                        </div>
                        <div className="text-xs text-slate-500">Characters with long gaps</div>
                    </div>
                </div>
            </div>
        </div>
    );
};

// --- Main Application ---

const DEFAULT_SCRIPT = "";

export default function ScriptBreakdownApp() {
  const [scriptText, setScriptText] = useState(DEFAULT_SCRIPT);
  const [activeTab, setActiveTab] = useState('dashboard');
  const [analysis, setAnalysis] = useState(null);
  const [isAnalyzing, setIsAnalyzing] = useState(false);
  const [isPdfLoading, setIsPdfLoading] = useState(false);
  const [pdfError, setPdfError] = useState(null);
  const [framework, setFramework] = useState('8-3');
  const [showRawText, setShowRawText] = useState(false);
  const [detectedPageCount, setDetectedPageCount] = useState(null);
  const [isJspdfLoaded, setIsJspdfLoaded] = useState(false);

  // Modal State
  const [modalOpen, setModalOpen] = useState(false);
  const [modalData, setModalData] = useState({ title: '', content: '', feedback: [] });

  // AI Analysis State
  const [aiAnalysis, setAiAnalysis] = useState(null);
  const [isAiLoading, setIsAiLoading] = useState(false);
  const [aiError, setAiError] = useState(null);

  // Framework Analysis State (NEW)
  const [frameworkAnalysis, setFrameworkAnalysis] = useState(null);
  const [isFrameworkLoading, setIsFrameworkLoading] = useState(false);
  const [frameworkError, setFrameworkError] = useState(null);

  // Rewrite State
  const [rewriteModalOpen, setRewriteModalOpen] = useState(false);
  const [fullRewriteModalOpen, setFullRewriteModalOpen] = useState(false);
  const [currentBeatRewrite, setCurrentBeatRewrite] = useState(null);
  const [beatRewriteData, setBeatRewriteData] = useState(null);
  const [isRewriteLoading, setIsRewriteLoading] = useState(false);
  const [rewriteError, setRewriteError] = useState(null);
  const [approvedBeatRewrites, setApprovedBeatRewrites] = useState([]);
  const [fullRewriteResult, setFullRewriteResult] = useState(null);

  // Professor Conversation State
  const [professorConversation, setProfessorConversation] = useState([]);
  const [followUpQuestion, setFollowUpQuestion] = useState('');
  const [isAskingFollowUp, setIsAskingFollowUp] = useState(false);

  const fileInputRef = useRef(null);

  // --- External Libraries Loading ---
  useEffect(() => {
    const pdfScript = document.createElement('script');
    pdfScript.src = 'https://cdnjs.cloudflare.com/ajax/libs/pdf.js/3.11.174/pdf.min.js';
    pdfScript.async = true;
    pdfScript.onload = () => {
      if (window.pdfjsLib) {
        window.pdfjsLib.GlobalWorkerOptions.workerSrc = 'https://cdnjs.cloudflare.com/ajax/libs/pdf.js/3.11.174/pdf.worker.min.js';
      }
    };
    document.body.appendChild(pdfScript);

    const jspdfScript = document.createElement('script');
    jspdfScript.src = 'https://cdnjs.cloudflare.com/ajax/libs/jspdf/2.5.1/jspdf.umd.min.js';
    jspdfScript.async = true;
    jspdfScript.onload = () => {
        setIsJspdfLoaded(true);
    };
    document.body.appendChild(jspdfScript);

    return () => {
        if(document.body.contains(pdfScript)) document.body.removeChild(pdfScript);
        if(document.body.contains(jspdfScript)) document.body.removeChild(jspdfScript);
    };
  }, []);

  const handleFileUpload = async (event) => {
    const file = event.target.files[0];
    if (!file) return;
    if (file.type !== 'application/pdf') {
      setPdfError("Please upload a valid PDF file.");
      return;
    }
    setPdfError(null);
    setIsPdfLoading(true);

    try {
      const arrayBuffer = await file.arrayBuffer();
      if (!window.pdfjsLib) throw new Error("PDF Library not loaded yet.");
      const pdf = await window.pdfjsLib.getDocument(arrayBuffer).promise;

      const exactPageCount = pdf.numPages;
      setDetectedPageCount(exactPageCount);

      let fullText = "";

      for (let i = 1; i <= pdf.numPages; i++) {
        const page = await pdf.getPage(i);
        const textContent = await page.getTextContent();

        const lines = [];
        const TOLERANCE = 6;

        textContent.items.forEach(item => {
            if (!item.str.trim()) return;

            const y = item.transform[5];
            const x = item.transform[4];
            const width = item.width;

            const existingLine = lines.find(l => Math.abs(l.y - y) < TOLERANCE);

            if (existingLine) {
                existingLine.items.push({ x, str: item.str, width });
            } else {
                lines.push({ y, items: [{ x, str: item.str, width }] });
            }
        });

        lines.sort((a, b) => b.y - a.y);

        const pageText = lines.map(line => {
            line.items.sort((a, b) => a.x - b.x);

            let lineStr = '';
            let lastX = -1;
            let lastWidth = 0;

            line.items.forEach(item => {
                if (lastX !== -1) {
                    const gap = item.x - (lastX + lastWidth);
                    if (gap > 2) {
                        lineStr += ' ';
                    }
                }
                lineStr += item.str;
                lastX = item.x;
                lastWidth = item.width || 0;
            });
            return lineStr;
        }).join('\n');

        fullText += `[[PAGE_${i}]]\n` + pageText + "\n\n";
      }

      if (fullText.trim().length < 50 && pdf.numPages > 0) {
          setPdfError("This appears to be a scanned PDF (image-based). Please convert it to text first or use an OCR tool.");
      } else {
          setScriptText(fullText);
          setTimeout(() => handleAnalyze(fullText, exactPageCount), 500);
      }

    } catch (err) {
      console.error("PDF Error:", err);
      setPdfError("Failed to parse PDF. Ensure it is a text-based PDF.");
    } finally {
      setIsPdfLoading(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const handleTextChange = (e) => {
      setScriptText(e.target.value);
      setDetectedPageCount(null);
  };

  // --- Modal Helpers ---
  const openSceneDetails = (scene, customTitle = null) => {
      if (!scene) return;

      const feedback = [];

      if (scene.intensityScore > 8) {
          feedback.push({ type: 'positive', title: 'High Intensity', text: 'Great conflict/action density here. Keeps the reader engaged.' });
      } else if (scene.intensityScore < 2 && scene.text.length > 10) {
          feedback.push({ type: 'critique', title: 'Low Conflict', text: 'This scene feels static. Consider adding a physical obstacle or raising the stakes.' });
      }

      if (scene.text.length > 40) {
           feedback.push({ type: 'critique', title: 'Running Long', text: 'This scene is quite long (> 1 page). Ensure the turn justifies the length.' });
      }

      if (scene.dialogueLines > 0 && scene.actionLines === 0) {
          feedback.push({ type: 'critique', title: 'Talking Heads', text: 'Pure dialogue. Add some business or action to break it up.' });
      }

      setModalData({
          title: customTitle || `Scene ${scene.id}: ${scene.header}`,
          content: scene.text.join('\n'),
          feedback: feedback
      });
      setModalOpen(true);
  };

  // --- Parsing Logic ---

  const normalizeTime = (rawTime) => {
      if (!rawTime) return 'UNKNOWN';
      const upper = rawTime.toUpperCase();
      if (upper.includes('MORNING')) return 'MORNING';
      if (upper.includes('AFTERNOON')) return 'AFTERNOON';
      if (upper.includes('EVENING')) return 'EVENING';
      if (upper.includes('NIGHT')) return 'NIGHT';
      if (upper.includes('DAY')) return 'DAY';
      return 'DAY'; // Default to Day for Continuous/Moments Later if ambiguous
  };

  const parseScript = (text, pageCountOverride = null) => {
    const lines = text.split('\n');
    const scenes = [];
    const characters = {};
    const locations = {};
    const props = {};
    const arcData = [];

    let currentScene = null;
    let currentSpeaker = null;
    let currentPage = 1;
    let hasPageMarkers = false;

    const sceneHeaderRegex = /^\s*(?:(?:\d+[A-Z]*|[A-Z]+\d+)[.\-\s]*)?(INT\.|EXT\.|INT[\s-]|EXT[\s-]|I\/E|INT\/EXT|INTERIOR|EXTERIOR)/i;
    const characterRegex = /^[A-Z][A-Z0-9\s\(\)]+$/;
    const transitionRegex = /^(FADE|CUT|DISSOLVE|SMASH|BACK TO|MATCH CUT)/;
    const pageMarkerRegex = /^\[\[PAGE_(\d+)\]\]$/;

    const findProps = (line) => {
      const capsWords = line.match(/\b[A-Z]{3,}\b/g);
      if (capsWords) {
        capsWords.forEach(word => {
          if (!['INT', 'EXT', 'DAY', 'NIGHT', 'CONTINUOUS', 'THE', 'AND', 'V.O.', 'O.S.', 'MOMENTS', 'LATER'].includes(word)) {
            props[word] = (props[word] || 0) + 1;
          }
        });
      }
    };

    lines.forEach((line, index) => {
      const trimmed = line.trim();

      if (!trimmed) {
          currentSpeaker = null;
          return;
      }

      const pageMatch = trimmed.match(pageMarkerRegex);
      if (pageMatch) {
          currentPage = parseInt(pageMatch[1]);
          hasPageMarkers = true;
          return;
      }

      if (trimmed.match(/^\d+\.$/) || trimmed.match(/^Page \d+$/i)) return;

      const sceneMatch = trimmed.match(sceneHeaderRegex);

      if (sceneMatch) {
        if (currentScene) scenes.push(currentScene);

        let cleanLine = trimmed;
        cleanLine = cleanLine.replace(/^(\d+[A-Z]*|[A-Z]+\d+)\s+/, '');

        const parts = cleanLine.split(/[-–—]/);
        const rawTime = parts.length > 1 ? parts[parts.length - 1].trim() : 'UNKNOWN';
        const simplifiedTime = normalizeTime(rawTime);
        const isNight = simplifiedTime === 'NIGHT';

        let locPart = cleanLine;
        locPart = locPart.replace(sceneHeaderRegex, '').trim();
        if (parts.length > 1) {
            locPart = locPart.replace(parts[parts.length-1], '').trim();
            locPart = locPart.replace(/[-–—]$/, '').trim();
        }

        const thisPage = hasPageMarkers ? currentPage : Math.floor(index / 55) + 1;

        currentScene = {
          id: scenes.length + 1,
          header: cleanLine,
          location: locPart || "UNKNOWN LOCATION",
          time: simplifiedTime,
          type: cleanLine.toUpperCase().includes('INT') ? 'Interior' : 'Exterior',
          isNight,
          characters: new Set(),
          props: new Set(),
          actionLines: 0,
          dialogueLines: 0,
          intensityScore: 0,
          pageEstimate: thisPage,
          text: []
        };

        if (!locations[currentScene.location]) locations[currentScene.location] = { count: 0, type: currentScene.type };
        locations[currentScene.location].count++;

        arcData.push({ scene: scenes.length + 1, intensity: 0, header: currentScene.location });
        currentSpeaker = null;
      }
      else if (characterRegex.test(trimmed) && !transitionRegex.test(trimmed) && !trimmed.includes(' - ') && !trimmed.includes('..') && trimmed.length < 40) {
        if (/[A-Z]/.test(trimmed)) {
            const name = trimmed.split('(')[0].trim();
            if (name.length > 1 && name !== 'CONTINUED') {
                currentSpeaker = name;
                if (currentScene) currentScene.characters.add(name);
                if (!characters[name]) characters[name] = { lines: 0, scenes: new Set(), words: 0 };
                characters[name].scenes.add(currentScene ? currentScene.id : 0);
            }
        }
      }
      else {
        if (currentScene) {
          currentScene.text.push(trimmed);
          if (currentSpeaker) {
            if (characters[currentSpeaker]) {
              characters[currentSpeaker].lines++;
              characters[currentSpeaker].words += trimmed.split(' ').length;
            }
            currentScene.dialogueLines++;
          } else {
            currentScene.actionLines++;
            findProps(trimmed);
            if (trimmed.includes('!')) currentScene.intensityScore += 2;
            if (trimmed.includes('?')) currentScene.intensityScore += 1;
            if (/(run|gun|shoot|fight|chase|blood|scream|kill|explode|crash|kiss|cry|bite|argue|confront|escape|grab|punch|slap|stab|shatter|collapse|desperate|panic|rush|slam|tear|break|threaten|attack|defend|embrace|confess|betray|reveal|discover|realize)/i.test(trimmed)) currentScene.intensityScore += 4;
          }
        }
      }
    });

    if (currentScene) scenes.push(currentScene);

    scenes.forEach((s, i) => {
      if (arcData[i]) {
        const density = (s.actionLines + s.dialogueLines) > 0 ? (s.intensityScore / (s.actionLines + s.dialogueLines)) * 10 : 0;
        arcData[i].intensity = Math.min(10, Math.max(1, density + (s.characters.size * 0.5)));
      }
    });

    const totalPages = pageCountOverride || (hasPageMarkers ? currentPage : Math.ceil(lines.length / 55));

    // --- Story Framework Logic ---

    const stcBeats = [
      { name: "Opening Image", pct: 0.01, description: "Sets the tone, mood, and style." },
      { name: "Theme Stated", pct: 0.05, description: "The underlying message of the story." },
      { name: "Set-up", pct: 0.10, description: "Introduction to the hero and their world." },
      { name: "Catalyst", pct: 0.12, description: "The life-changing event." },
      { name: "Debate", pct: 0.20, description: "Hero doubts the journey." },
      { name: "Break into Two", pct: 0.25, description: "Hero leaves the old world." },
      { name: "B Story", pct: 0.30, description: "Introduction of the love interest/mentor." },
      { name: "Fun and Games", pct: 0.40, description: "The promise of the premise." },
      { name: "Midpoint", pct: 0.50, description: "Stakes rise, clock starts ticking." },
      { name: "Bad Guys Close In", pct: 0.60, description: "Internal and external forces tighten." },
      { name: "All Is Lost", pct: 0.75, description: "False defeat. Whiff of death." },
      { name: "Dark Night of Soul", pct: 0.80, description: "Hero wallows, then finds answer." },
      { name: "Break into Three", pct: 0.85, description: "Hero fixes the problem." },
      { name: "Finale", pct: 0.90, description: "Final showdown." },
      { name: "Final Image", pct: 1.00, description: "Mirror of opening, showing change." },
    ];

    const stcAnalysis = stcBeats.map(beat => {
        const targetPage = Math.round(beat.pct * totalPages);
        const matchingScene = scenes.find(s => Math.abs(s.pageEstimate - targetPage) < 2) ||
                              scenes.find(s => s.pageEstimate >= targetPage);
        return { ...beat, targetPage, scene: matchingScene };
    });

    const seqLength = totalPages / 8;
    const sequences83 = [];

    for (let i = 0; i < 8; i++) {
        const startPage = i * seqLength;
        const endPage = (i + 1) * seqLength;

        const seqScenes = scenes.filter(s => s.pageEstimate >= startPage && (i === 7 ? s.pageEstimate <= endPage : s.pageEstimate < endPage));

        const actionScene = seqScenes.length > 0 ? seqScenes[0] : null;
        const reactionScene = seqScenes.length > 2 ?
            seqScenes.slice(1, -1).reduce((prev, curr) => (curr.dialogueLines > prev.dialogueLines ? curr : prev), seqScenes[1])
            : null;
        const dustScene = seqScenes.length > 0 ? seqScenes[seqScenes.length - 1] : null;

        sequences83.push({
            id: i + 1,
            range: `${Math.round(startPage)}-${Math.round(endPage)}`,
            scenes: seqScenes,
            ard: { action: actionScene, reaction: reactionScene, dust: dustScene }
        });
    }

    // --- Professor Feedback Generation ---

    const generateMcKeeFeedback = () => {
        const feedback = [];
        const actionCount = scenes.reduce((acc, s) => acc + s.actionLines, 0);
        const dialogueCount = scenes.reduce((acc, s) => acc + s.dialogueLines, 0);
        const ratio = dialogueCount / (actionCount || 1);

        if (ratio > 2.5) {
            feedback.push({
                text: "Your script is drowning in words. Film is a visual medium. You are telling me the story, not showing me the conflict. Cut the speeches and find the physical action that expresses the subtext.",
                fix: "Review your dialogue-heavy scenes (likely Act 2). Can any speech be replaced by a look, a gesture, or a prop? Aim for a 1:1 action-to-dialogue ratio."
            });
        } else {
            feedback.push({
                text: "Good balance of visual action and dialogue. You are allowing the subtext to breathe through behavior.",
                fix: null,
                type: 'praise'
            });
        }

        const act2Scenes = scenes.slice(Math.floor(scenes.length * 0.25), Math.floor(scenes.length * 0.75));
        const avgAct2Intensity = act2Scenes.reduce((acc, s) => acc + s.intensityScore, 0) / (act2Scenes.length || 1);

        if (avgAct2Intensity < 3) {
            feedback.push({
                text: "Your Second Act is soft. The 'Gap' between expectation and result is missing. The protagonist must face escalating forces of antagonism, but here they seem to be coasting.",
                fix: "Add a 'False Victory' followed immediately by a 'Complication' around the Midpoint (approx page " + Math.round(totalPages/2) + "). Make the hero fail harder."
            });
        }

        const sceneLengths = scenes.map(s => s.text.length);
        const variance = sceneLengths.reduce((sum, val) => sum + Math.pow(val - (sceneLengths.reduce((a,b)=>a+b,0)/sceneLengths.length), 2), 0) / sceneLengths.length;

        if (variance < 50) {
             feedback.push({
                text: "Your pacing is monotonous. Every scene feels the same length. You need a rhythm of short, punchy scenes mixed with longer, dramatic ones.",
                fix: "Combine two short scenes into one continuous sequence, or break a long dialogue scene into three location shifts to add kinetic energy."
            });
        }

        const weakScenes = scenes.filter(s => s.intensityScore < 2 && s.text.length > 10).slice(0, 3);
        if (weakScenes.length > 0) {
             feedback.push({
                text: `You have scenes that are dead weight. They lack conflict. Specifically: Scene ${weakScenes[0].id} ('${weakScenes[0].header}').`,
                fix: `In Scene ${weakScenes[0].id}, give a character a secret they must keep, or an urgent physical goal they must achieve immediately.`
            });
        }

        return feedback;
    };

    const generateSnyderFeedback = () => {
        const feedback = [];

        // Helper to find scene nearest to a percentage
        const findSceneAtPercent = (pct) => {
            const targetPage = Math.round(pct * totalPages);
            return scenes.find(s => Math.abs(s.pageEstimate - targetPage) <= 2) ||
                   scenes.find(s => s.pageEstimate >= targetPage - 2 && s.pageEstimate <= targetPage + 2);
        };

        // Helper to get intensity at a percentage range
        const getIntensityInRange = (startPct, endPct) => {
            const startPage = Math.round(startPct * totalPages);
            const endPage = Math.round(endPct * totalPages);
            const rangeScenes = scenes.filter(s => s.pageEstimate >= startPage && s.pageEstimate <= endPage);
            if (rangeScenes.length === 0) return 0;
            return rangeScenes.reduce((acc, s) => acc + s.intensityScore, 0) / rangeScenes.length;
        };

        // 1. OPENING IMAGE CHECK (1%)
        const openingScene = scenes[0];
        if (openingScene) {
            if (openingScene.intensityScore >= 3 || openingScene.text.length >= 5) {
                feedback.push({
                    beat: "Opening Image",
                    status: "ok",
                    text: "Your opening scene has visual weight. Good start.",
                    targetPage: 1,
                    actualPage: openingScene.pageEstimate
                });
            } else {
                feedback.push({
                    beat: "Opening Image",
                    status: "warning",
                    text: "Your opening feels thin. The first image should establish tone, mood, and visual style.",
                    targetPage: 1,
                    actualPage: openingScene.pageEstimate,
                    fix: "Add specific visual details or action that captures the world of your story."
                });
            }
        }

        // 2. CATALYST CHECK (~12% or page 12 on 100-page script)
        const catalystTarget = Math.round(0.12 * totalPages);
        const catalystScene = findSceneAtPercent(0.12);
        const preCatalystIntensity = getIntensityInRange(0, 0.10);
        const catalystIntensity = getIntensityInRange(0.10, 0.15);

        if (catalystIntensity > preCatalystIntensity + 2) {
            feedback.push({
                beat: "Catalyst",
                status: "ok",
                text: `Nice intensity spike around page ${catalystTarget}. The life-changing event lands.`,
                targetPage: catalystTarget,
                actualPage: catalystScene?.pageEstimate
            });
        } else {
            feedback.push({
                beat: "Catalyst",
                status: "warning",
                text: `Your Catalyst is buried or missing. By page ${catalystTarget}, something must FORCE the hero to act.`,
                targetPage: catalystTarget,
                actualPage: catalystScene?.pageEstimate,
                fix: "Add a clear inciting incident: a death, an arrival, a discovery, a threat. Make it unavoidable."
            });
        }

        // 3. BREAK INTO TWO CHECK (~25%)
        const breakTwoTarget = Math.round(0.25 * totalPages);
        const act1Scenes = scenes.filter(s => s.pageEstimate <= breakTwoTarget);
        const act2Start = scenes.filter(s => s.pageEstimate > breakTwoTarget && s.pageEstimate <= breakTwoTarget + 3);

        // Check for location change (new world)
        const act1Locations = new Set(act1Scenes.map(s => s.location));
        const newLocations = act2Start.filter(s => !act1Locations.has(s.location));

        if (newLocations.length > 0) {
            feedback.push({
                beat: "Break into Two",
                status: "ok",
                text: `Good transition around page ${breakTwoTarget}. New location suggests the hero entering a new world.`,
                targetPage: breakTwoTarget,
                actualPage: newLocations[0]?.pageEstimate
            });
        } else {
            feedback.push({
                beat: "Break into Two",
                status: "warning",
                text: `The Act 1/2 break around page ${breakTwoTarget} lacks punch. The hero should enter an unfamiliar world.`,
                targetPage: breakTwoTarget,
                fix: "Consider a new location, a journey, or a clear 'threshold crossing' moment."
            });
        }

        // 4. MIDPOINT CHECK (50%)
        const midpointTarget = Math.round(0.50 * totalPages);
        const midpointScene = findSceneAtPercent(0.50);
        const midpointIntensity = getIntensityInRange(0.48, 0.52);
        const prePostMidAvg = (getIntensityInRange(0.40, 0.48) + getIntensityInRange(0.52, 0.60)) / 2;

        if (midpointIntensity > prePostMidAvg + 1) {
            feedback.push({
                beat: "Midpoint",
                status: "ok",
                text: `Strong Midpoint around page ${midpointTarget}. Stakes are raised.`,
                targetPage: midpointTarget,
                actualPage: midpointScene?.pageEstimate
            });
        } else {
            feedback.push({
                beat: "Midpoint",
                status: "warning",
                text: `Your Midpoint (page ${midpointTarget}) is flat. This should be a false victory OR false defeat.`,
                targetPage: midpointTarget,
                actualPage: midpointScene?.pageEstimate,
                fix: "Add a party scene, a public triumph, a relationship peak—then immediately complicate it."
            });
        }

        // 5. ALL IS LOST CHECK (~75%)
        const allIsLostTarget = Math.round(0.75 * totalPages);
        const allIsLostScene = findSceneAtPercent(0.75);
        const allIsLostIntensity = getIntensityInRange(0.73, 0.77);
        const act2Intensity = getIntensityInRange(0.50, 0.73);

        // Looking for HIGH intensity (despair) followed by low (dark night)
        const darkNightIntensity = getIntensityInRange(0.77, 0.83);

        if (allIsLostIntensity > act2Intensity && darkNightIntensity < allIsLostIntensity) {
            feedback.push({
                beat: "All Is Lost",
                status: "ok",
                text: `The "whiff of death" hits around page ${allIsLostTarget}. Hero is defeated.`,
                targetPage: allIsLostTarget,
                actualPage: allIsLostScene?.pageEstimate
            });
        } else {
            feedback.push({
                beat: "All Is Lost",
                status: "warning",
                text: `Missing the gut-punch at page ${allIsLostTarget}. The hero needs to lose EVERYTHING here.`,
                targetPage: allIsLostTarget,
                actualPage: allIsLostScene?.pageEstimate,
                fix: "Kill a mentor, destroy a relationship, reveal a devastating truth. Make it hurt."
            });
        }

        // 6. FINALE CHECK (~85-100%)
        const finaleScenes = scenes.filter(s => s.pageEstimate >= totalPages * 0.85);
        const finaleIntensity = finaleScenes.reduce((acc, s) => acc + s.intensityScore, 0) / (finaleScenes.length || 1);

        if (finaleIntensity >= 4) {
            feedback.push({
                beat: "Finale",
                status: "ok",
                text: "Your finale has energy. The final battle/confrontation delivers.",
                targetPage: Math.round(totalPages * 0.90),
                actualPage: finaleScenes[0]?.pageEstimate
            });
        } else {
            feedback.push({
                beat: "Finale",
                status: "warning",
                text: "The finale lacks intensity. After the Dark Night, the hero must FIGHT with everything.",
                targetPage: Math.round(totalPages * 0.90),
                fix: "Raise physical stakes, add time pressure, make the antagonist more formidable."
            });
        }

        // 7. PRIMAL CHECK (overall script)
        const allText = scenes.map(s => s.text.join(' ')).join(' ').toLowerCase();
        const primalKeywords = {
            survival: /(die|death|kill|survive|alive|murder|blood|fear|danger|threat)/gi,
            love: /(love|heart|kiss|together|marry|romance|passion|desire)/gi,
            revenge: /(revenge|payback|justice|betray|wrong|punish|hate)/gi,
            ambition: /(power|money|success|win|rich|fame|best|champion)/gi,
            family: /(family|father|mother|son|daughter|child|brother|sister|home)/gi
        };

        let strongestPrimal = null;
        let highestCount = 0;

        for (const [theme, regex] of Object.entries(primalKeywords)) {
            const matches = allText.match(regex) || [];
            if (matches.length > highestCount) {
                highestCount = matches.length;
                strongestPrimal = theme;
            }
        }

        if (highestCount >= 10) {
            feedback.push({
                beat: "Primal Drive",
                status: "ok",
                text: `Strong primal theme detected: ${strongestPrimal.toUpperCase()}. This connects to universal human needs.`,
                targetPage: null,
                actualPage: null
            });
        } else {
            feedback.push({
                beat: "Primal Drive",
                status: "warning",
                text: "What's the primal, universal need? Every great movie is about survival, love, revenge, or ambition at its core.",
                targetPage: null,
                fix: "Thread a clear primal motivation throughout: What does your hero NEED at a gut level?"
            });
        }

        return feedback;
    };

    return {
      scenes,
      characters: Object.entries(characters).map(([name, data]) => ({
        name,
        lines: data.lines,
        sceneCount: data.scenes.size,
        wordCount: data.words,
        sceneAppearances: Array.from(data.scenes).sort((a, b) => a - b),
        firstAppearance: Math.min(...Array.from(data.scenes)),
        lastAppearance: Math.max(...Array.from(data.scenes)),
        gaps: (() => {
          const sorted = Array.from(data.scenes).sort((a, b) => a - b);
          const gaps = [];
          for (let i = 1; i < sorted.length; i++) {
            const gap = sorted[i] - sorted[i-1];
            if (gap > 5) gaps.push({ after: sorted[i-1], before: sorted[i], size: gap });
          }
          return gaps;
        })()
      })).sort((a, b) => b.lines - a.lines),
      locations: Object.entries(locations).map(([name, data]) => ({
        name,
        count: data.count,
        type: data.type
      })).sort((a, b) => b.count - a.count),
      props: Object.entries(props).map(([name, count]) => ({ name, count })).sort((a, b) => b.count - a.count).slice(0, 10),
      arcData,
      totalScenes: scenes.length,
      totalPages,
      stcAnalysis,
      sequences83,
      professorFeedback: generateMcKeeFeedback(),
      snyderFeedback: generateSnyderFeedback()
    };
  };

  const handleAnalyze = (textToAnalyze = scriptText, pageCountOverride = detectedPageCount) => {
    setIsAnalyzing(true);
    const text = typeof textToAnalyze === 'string' ? textToAnalyze : scriptText;
    setTimeout(() => {
      const data = parseScript(text, pageCountOverride);
      setAnalysis(data);
      setIsAnalyzing(false);
      setActiveTab('dashboard');
    }, 800);
  };

  // --- PDF Generation ---
  const generatePDF = () => {
    if (!analysis || !window.jspdf) return;

    const { jsPDF } = window.jspdf;
    const doc = new jsPDF();
    let yPos = 20;
    const margin = 20;
    const pageWidth = doc.internal.pageSize.getWidth();
    const contentWidth = pageWidth - (margin * 2);

    const checkPageBreak = (heightNeeded) => {
        if (yPos + heightNeeded > 280) {
            doc.addPage();
            yPos = 20;
        }
    }

    // Title
    doc.setFontSize(22);
    doc.setTextColor(40);
    doc.text("Director's Lens Report", margin, yPos);
    yPos += 10;

    doc.setFontSize(12);
    doc.setTextColor(100);
    doc.text(`Generated on ${new Date().toLocaleDateString()}`, margin, yPos);
    yPos += 15;

    // Stats
    doc.setFontSize(16);
    doc.setTextColor(0);
    doc.text("Script Overview", margin, yPos);
    yPos += 10;

    doc.setFontSize(12);
    doc.text(`Total Pages: ${analysis.totalPages}`, margin, yPos);
    doc.text(`Total Scenes: ${analysis.totalScenes}`, margin + 60, yPos);
    yPos += 8;
    doc.text(`Speaking Parts: ${analysis.characters.length}`, margin, yPos);
    doc.text(`Locations: ${analysis.locations.length}`, margin + 60, yPos);
    yPos += 15;

    // Professor Feedback
    checkPageBreak(60);
    doc.setFontSize(16);
    doc.text("The Professor's Critique", margin, yPos);
    yPos += 10;

    doc.setFontSize(10);
    analysis.professorFeedback.forEach(item => {
        const text = `• ${item.text}`;
        const splitText = doc.splitTextToSize(text, contentWidth);
        doc.text(splitText, margin, yPos);
        yPos += (splitText.length * 5);

        if (item.fix) {
            doc.setTextColor(200, 100, 0);
            const fixText = `  FIX: ${item.fix}`;
            const splitFix = doc.splitTextToSize(fixText, contentWidth);
            doc.text(splitFix, margin, yPos);
            doc.setTextColor(0);
            yPos += (splitFix.length * 5) + 5;
        } else {
            yPos += 5;
        }
        checkPageBreak(20);
    });

    // 8-3 Framework Analysis
    checkPageBreak(60);
    doc.setFontSize(16);
    doc.text("8-3 Framework Analysis", margin, yPos);
    yPos += 10;

    analysis.sequences83.forEach(seq => {
        checkPageBreak(60);
        doc.setFontSize(12);
        doc.setFont(undefined, 'bold');
        doc.setTextColor(0);
        doc.text(`Sequence ${seq.id} (Pages ${seq.range})`, margin, yPos);
        yPos += 7;

        const printBeat = (label, scene, color) => {
            doc.setFontSize(10);
            doc.setFont(undefined, 'bold');
            doc.setTextColor(...color);
            doc.text(`${label}:`, margin + 5, yPos);

            doc.setFont(undefined, 'normal');
            doc.setTextColor(60);
            if (scene) {
                const headerText = `${scene.header}`;
                doc.text(headerText, margin + 35, yPos);
                yPos += 5;

                const snippet = scene.text.slice(0, 2).join(' ').substring(0, 120) + "...";
                const splitSnippet = doc.splitTextToSize(snippet, contentWidth - 35);
                doc.setFontSize(9);
                doc.setTextColor(100);
                doc.text(splitSnippet, margin + 35, yPos);
                yPos += (splitSnippet.length * 4) + 2;
            } else {
                doc.text("Not detected in this sequence.", margin + 35, yPos);
                yPos += 6;
            }
        };

        printBeat("ACTION", seq.ard.action, [22, 163, 74]); // Green
        printBeat("REACTION", seq.ard.reaction, [37, 99, 235]); // Blue
        printBeat("DUST", seq.ard.dust, [234, 88, 12]); // Orange

        yPos += 5;
    });

    // Save The Cat Analysis (Always Included)
    checkPageBreak(60);
    doc.addPage(); // Force page break for clarity
    yPos = 20;
    doc.setFontSize(16);
    doc.setTextColor(0);
    doc.setFont(undefined, 'bold');
    doc.text("Save The Cat Breakdown", margin, yPos);
    yPos += 10;

    analysis.stcAnalysis.forEach(beat => {
        checkPageBreak(20);
        doc.setFontSize(11);
        doc.setFont(undefined, 'bold');
        doc.text(`${beat.name} (Ideal: pg ${beat.targetPage})`, margin, yPos);
        doc.setFont(undefined, 'normal');
        const matchText = beat.scene ? `Found: Scene ${beat.scene.id} (Est pg ${Math.round(beat.scene.pageEstimate)})` : "Not found in range";
        doc.text(matchText, margin + 80, yPos);
        yPos += 7;
    });

    // Score Explainer
    checkPageBreak(40);
    doc.addPage();
    yPos = 20;
    doc.setFontSize(16);
    doc.setTextColor(0);
    doc.setFont(undefined, 'bold');
    doc.text("Scene Intensity Score Guide", margin, yPos);
    yPos += 10;
    doc.setFontSize(10);
    doc.setFont(undefined, 'normal');
    doc.text("The Score calculates the dramatic energy of a scene based on text triggers:", margin, yPos);
    yPos += 7;
    doc.text("• +4 pts: Action words (fight, run, scream, kiss, blood)", margin + 5, yPos);
    yPos += 5;
    doc.text("• +2 pts: Exclamation marks (shouting/surprise)", margin + 5, yPos);
    yPos += 5;
    doc.text("• +1 pt: Question marks (conflict/interrogation)", margin + 5, yPos);
    yPos += 10;
    doc.setFont(undefined, 'bold');
    doc.text("Scale: 0-5 (Low/Calm), 6-15 (Medium/Drama), 16+ (High/Action)", margin, yPos);
    yPos += 15;

    // Script Breakdown Section
    checkPageBreak(60);
    doc.setFontSize(16);
    doc.setFont(undefined, 'bold');
    doc.text("Detailed Scene Breakdown", margin, yPos);
    yPos += 10;

    doc.setFontSize(10);
    doc.setFont(undefined, 'normal');

    // Header
    doc.setFillColor(240, 240, 240);
    doc.rect(margin, yPos, contentWidth, 8, 'F');
    doc.setFont(undefined, 'bold');
    doc.text("#", margin + 2, yPos + 5);
    doc.text("Scene Header", margin + 15, yPos + 5);
    doc.text("I/E", margin + 120, yPos + 5);
    doc.text("Time", margin + 140, yPos + 5);
    doc.text("Score", margin + 160, yPos + 5);
    yPos += 10;

    doc.setFont(undefined, 'normal');
    analysis.scenes.forEach(scene => {
        checkPageBreak(10);

        doc.text(scene.id.toString(), margin + 2, yPos);

        let header = scene.header;
        if (header.length > 55) header = header.substring(0, 52) + "...";
        doc.text(header, margin + 15, yPos);

        doc.text(scene.type.substring(0, 3).toUpperCase(), margin + 120, yPos);
        doc.text(scene.time, margin + 140, yPos);
        doc.text(scene.intensityScore.toString(), margin + 165, yPos);

        yPos += 7;
    });

    doc.save("Director_Lens_Report.pdf");
  };

  // --- AI Analysis ---
  const requestAiAnalysis = async () => {
    if (!analysis) return;

    setIsAiLoading(true);
    setAiError(null);
    setAiAnalysis(null);

    // Prepare comprehensive script data with ALL scene information
    const scriptSummary = {
        overview: {
            totalPages: analysis.totalPages,
            totalScenes: analysis.totalScenes,
            characterCount: analysis.characters.length,
            locationCount: analysis.locations.length
        },
        topCharacters: analysis.characters.slice(0, 8).map(c => ({
            name: c.name,
            lines: c.lines,
            scenes: c.sceneCount
        })),
        topLocations: analysis.locations.slice(0, 6).map(l => ({
            name: l.name,
            count: l.count,
            type: l.type
        })),
        // Include ALL scenes with their intensity scores for comprehensive analysis
        allScenes: analysis.scenes.map((s, idx) => ({
            sceneNumber: idx + 1,
            header: s.header,
            intensity: s.intensityScore,
            characters: Array.from(s.characters).slice(0, 5),
            pageEstimate: s.pageEstimate,
            dialogueLines: s.dialogueLines,
            actionLines: s.actionLines
        })),
        // Low intensity scenes that might be dead weight
        lowIntensityScenes: analysis.scenes
            .map((s, idx) => ({ ...s, sceneNumber: idx + 1 }))
            .filter(s => s.intensityScore < 2 && s.text.length > 5)
            .map(s => ({
                sceneNumber: s.sceneNumber,
                header: s.header,
                intensity: s.intensityScore
            })),
        openingScenes: analysis.scenes.slice(0, 3).map((s, idx) => ({
            sceneNumber: idx + 1,
            header: s.header,
            intensity: s.intensityScore,
            characters: Array.from(s.characters).slice(0, 3),
            snippet: s.text.slice(0, 3).join(' ').substring(0, 200)
        })),
        midpointScene: (() => {
            const midIdx = Math.floor(analysis.scenes.length / 2);
            const mid = analysis.scenes[midIdx];
            return mid ? {
                sceneNumber: midIdx + 1,
                header: mid.header,
                intensity: mid.intensityScore,
                characters: Array.from(mid.characters).slice(0, 3),
                snippet: mid.text.slice(0, 3).join(' ').substring(0, 200)
            } : null;
        })(),
        closingScenes: analysis.scenes.slice(-2).map((s, idx) => ({
            sceneNumber: analysis.scenes.length - 2 + idx + 1,
            header: s.header,
            intensity: s.intensityScore,
            characters: Array.from(s.characters).slice(0, 3),
            snippet: s.text.slice(0, 3).join(' ').substring(0, 200)
        })),
        intensityArc: {
            act1Avg: Math.round(analysis.scenes.slice(0, Math.floor(analysis.scenes.length * 0.25))
                .reduce((a, s) => a + s.intensityScore, 0) / (analysis.scenes.length * 0.25) * 10) / 10,
            act2Avg: Math.round(analysis.scenes.slice(Math.floor(analysis.scenes.length * 0.25), Math.floor(analysis.scenes.length * 0.75))
                .reduce((a, s) => a + s.intensityScore, 0) / (analysis.scenes.length * 0.5) * 10) / 10,
            act3Avg: Math.round(analysis.scenes.slice(Math.floor(analysis.scenes.length * 0.75))
                .reduce((a, s) => a + s.intensityScore, 0) / (analysis.scenes.length * 0.25) * 10) / 10,
            peakScene: analysis.scenes.reduce((max, s) => s.intensityScore > max.intensityScore ? s : max, analysis.scenes[0])?.header
        },
        existingFeedback: {
            professorNotes: analysis.professorFeedback.map(f => f.text).slice(0, 3),
            snyderWarnings: analysis.snyderFeedback.filter(f => f.status === 'warning').map(f => f.beat)
        }
    };

    const prompt = `You are "The Professor" - an expert screenplay consultant with the analytical rigor of Robert McKee and the commercial instincts of Blake Snyder. You've read thousands of scripts and consulted on major studio films. You are direct, thorough, and comprehensive in your feedback.

Analyze this screenplay data and provide SPECIFIC, ACTIONABLE feedback. When you identify issues, LIST ALL SCENES that exhibit the problem, not just one or two examples. Screenwriters need to see the full scope of issues to fix them properly.

SCRIPT DATA:
${JSON.stringify(scriptSummary, null, 2)}

CRITICAL: Notice the "lowIntensityScenes" array contains ALL scenes with low conflict/tension (intensity < 2). If you're critiquing dead weight scenes, you MUST list ALL of them, not just Scene 8 as an example.

EXISTING TOOL FEEDBACK (for context, don't repeat these):
- Professor notes: ${scriptSummary.existingFeedback.professorNotes.join('; ')}
- Snyder warnings on beats: ${scriptSummary.existingFeedback.snyderWarnings.join(', ')}

Provide your analysis in this exact JSON format:
{
  "overallGrade": "A/B+/B/C+/C/D/F",
  "headline": "One punchy sentence capturing the script's biggest strength or weakness",
  "commercialPotential": "High/Medium/Low with brief reason",
  "insights": [
    {
      "category": "Structure|Character|Pacing|Opening|Ending|Dialogue|Theme",
      "severity": "critical|warning|suggestion|praise",
      "title": "Brief title (5 words max)",
      "finding": "What you observed in the data. BE COMPREHENSIVE - if multiple scenes have an issue, list ALL scene numbers and headers, not just one example. Use format like: 'Scenes 8, 15, 23, 31, 42 (Scene headers: X, Y, Z...) all lack conflict.'",
      "recommendation": "Actionable fix or enhancement"
    }
  ],
  "genreGuess": "Your best guess at genre based on locations, intensity, keywords",
  "comparables": ["Movie 1", "Movie 2"]
}

Provide 5-8 insights. At least one should be praise (something working well). Be direct, thorough, and comprehensive - list ALL problematic scenes when you identify an issue. Don't hedge - screenwriters want honest, complete feedback.`;

    try {
        const response = await fetch("/api/analyze", {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({
                scriptSummary,
                prompt
            })
        });

        if (!response.ok) {
            throw new Error(`API error: ${response.status}`);
        }

        const data = await response.json();
        let responseText = data.content[0].text;

        // Clean up potential markdown formatting
        responseText = responseText.replace(/```json\n?/g, "").replace(/```\n?/g, "").trim();

        const parsed = JSON.parse(responseText);
        setAiAnalysis(parsed);

    } catch (err) {
        console.error("AI Analysis Error:", err);
        setAiError(err.message || "AI analysis failed. Please try again.");
    } finally {
        setIsAiLoading(false);
    }
  };

  // Follow-up question handler
  const askFollowUpQuestion = async () => {
    if (!followUpQuestion.trim() || !analysis || !aiAnalysis) return;

    setIsAskingFollowUp(true);
    const userQuestion = followUpQuestion;
    setFollowUpQuestion('');

    // Add user question to conversation
    setProfessorConversation(prev => [...prev, {
      role: 'user',
      content: userQuestion
    }]);

    try {
      const conversationHistory = professorConversation.map(msg =>
        `${msg.role === 'user' ? 'Student' : 'Professor'}: ${msg.content}`
      ).join('\n\n');

      const followUpPrompt = `You are "The Professor" continuing a conversation about this screenplay. The student has a follow-up question.

PREVIOUS ANALYSIS:
${JSON.stringify(aiAnalysis, null, 2)}

SCRIPT DATA (for reference):
Total Scenes: ${analysis.totalScenes}
Low Intensity Scenes: ${JSON.stringify(analysis.scenes
  .map((s, idx) => ({ sceneNumber: idx + 1, header: s.header, intensity: s.intensityScore }))
  .filter(s => s.intensity < 2 && s.intensity > 0)
)}

CONVERSATION SO FAR:
${conversationHistory}

STUDENT'S NEW QUESTION:
${userQuestion}

Provide a direct, helpful answer. If they're asking about specific scenes, reference the scene data. If they want more detail on something from your analysis, expand on it comprehensively. Respond in plain text (not JSON), 2-4 paragraphs.`;

      const response = await fetch("/api/analyze", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          scriptSummary: { question: userQuestion },
          prompt: followUpPrompt
        })
      });

      if (!response.ok) {
        throw new Error(`API error: ${response.status}`);
      }

      const data = await response.json();
      const professorReply = data.content?.[0]?.text || "I apologize, but I couldn't formulate a response. Please try rephrasing your question.";

      // Add professor's response to conversation
      setProfessorConversation(prev => [...prev, {
        role: 'professor',
        content: professorReply
      }]);

    } catch (err) {
      console.error("Follow-up question error:", err);
      setProfessorConversation(prev => [...prev, {
        role: 'professor',
        content: "I apologize, but I encountered an error processing your question. Please try again."
      }]);
    } finally {
      setIsAskingFollowUp(false);
    }
  };

  // NEW: Request 4-framework analysis
  const requestFrameworks = async () => {
    if (!analysis) return;

    setIsFrameworkLoading(true);
    setFrameworkError(null);
    setFrameworkAnalysis(null);

    try {
      const result = await requestFrameworkAnalysis(analysis);
      setFrameworkAnalysis(result);
    } catch (err) {
      console.error('Framework analysis error:', err);
      setFrameworkError(err.message || 'Failed to generate framework analysis');
    } finally {
      setIsFrameworkLoading(false);
    }
  };

  // Beat Rewrite Handlers
  const handleBeatRewrite = async (beat, frameworkType) => {
    if (!analysis || !analysis.scenes) return;

    setCurrentBeatRewrite({ beat, frameworkType });
    setRewriteModalOpen(true);
    setIsRewriteLoading(true);
    setRewriteError(null);
    setBeatRewriteData(null);

    try {
      const result = await requestBeatRewrite(beat, analysis.scenes, frameworkType);
      setBeatRewriteData(result);
    } catch (err) {
      console.error('Beat rewrite error:', err);
      setRewriteError(err.message || 'Failed to generate beat rewrite');
    } finally {
      setIsRewriteLoading(false);
    }
  };

  const handleAcceptBeatRewrite = () => {
    if (!beatRewriteData) return;

    // Add to approved rewrites
    setApprovedBeatRewrites(prev => [...prev, beatRewriteData]);

    // Close modal
    setRewriteModalOpen(false);
    setBeatRewriteData(null);
    setCurrentBeatRewrite(null);
  };

  const handleRejectBeatRewrite = () => {
    setRewriteModalOpen(false);
    setBeatRewriteData(null);
    setCurrentBeatRewrite(null);
  };

  // Full Script Rewrite Handlers
  const handleGenerateFullRewrite = async (preservationLevel) => {
    if (!analysis || !frameworkAnalysis) return;

    setIsRewriteLoading(true);
    setRewriteError(null);
    setFullRewriteResult(null);

    try {
      const result = await requestFullRewrite(
        scriptText,
        analysis,
        frameworkAnalysis,
        preservationLevel,
        approvedBeatRewrites
      );
      setFullRewriteResult(result);
    } catch (err) {
      console.error('Full rewrite error:', err);
      setRewriteError(err.message || 'Failed to generate full rewrite');
    } finally {
      setIsRewriteLoading(false);
    }
  };

  const handleAcceptFullRewrite = (rewriteResult) => {
    if (!rewriteResult || !rewriteResult.rewritten) return;

    // Update the script text with the rewritten version
    setScriptText(rewriteResult.rewritten);

    // Close modal
    setFullRewriteModalOpen(false);
    setFullRewriteResult(null);
    setApprovedBeatRewrites([]);

    // Re-analyze the new script
    setTimeout(() => {
      handleAnalyze();
    }, 500);
  };

  useEffect(() => {
    if (scriptText) handleAnalyze();
  }, []);

  // --- Render Functions ---

  const renderDashboard = () => (
    <div className="space-y-6 animate-in fade-in duration-500">
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card className="p-4 bg-gradient-to-br from-indigo-50 to-white">
          <div className="flex items-center space-x-2 text-indigo-600 mb-2">
            <FileText size={20} />
            <h3 className="font-semibold text-sm uppercase tracking-wider">Est. Pages</h3>
          </div>
          <p className="text-3xl font-bold text-slate-800">
            {analysis.totalPages}
            {detectedPageCount && <span className="text-xs font-normal text-slate-400 ml-2">(Exact from PDF)</span>}
          </p>
        </Card>
        <Card className="p-4 bg-gradient-to-br from-emerald-50 to-white">
          <div className="flex items-center space-x-2 text-emerald-600 mb-2">
            <Scissors size={20} />
            <h3 className="font-semibold text-sm uppercase tracking-wider">Total Scenes</h3>
          </div>
          <p className="text-3xl font-bold text-slate-800">{analysis.totalScenes}</p>
        </Card>
        <Card className="p-4 bg-gradient-to-br from-purple-50 to-white">
          <div className="flex items-center space-x-2 text-purple-600 mb-2">
            <Users size={20} />
            <h3 className="font-semibold text-sm uppercase tracking-wider">Speaking Parts</h3>
          </div>
          <p className="text-3xl font-bold text-slate-800">{analysis.characters.length}</p>
        </Card>
        <Card className="p-4 bg-gradient-to-br from-amber-50 to-white">
          <div className="flex items-center space-x-2 text-amber-600 mb-2">
            <MapPin size={20} />
            <h3 className="font-semibold text-sm uppercase tracking-wider">Unique Locations</h3>
          </div>
          <p className="text-3xl font-bold text-slate-800">{analysis.locations.length}</p>
        </Card>
      </div>

      <Card className="p-6">
        <div className="flex justify-between items-center mb-6">
          <h3 className="text-lg font-bold text-slate-800 flex items-center">
            <Activity className="mr-2 text-rose-500" /> Script Pacing & Intensity Arc
          </h3>
        </div>
        <div className="h-64 flex items-end justify-between gap-1 w-full">
          {analysis.arcData.map((scene, idx) => (
            <div key={idx} className="group relative flex-1 flex flex-col justify-end h-full">
               <div
                className={`w-full rounded-t-sm transition-all duration-300 ${scene.intensity > 6 ? 'bg-rose-400' : 'bg-blue-400'} group-hover:bg-indigo-600`}
                style={{ height: `${scene.intensity * 10}%` }}
              ></div>
              <div className="absolute bottom-full mb-2 left-1/2 -translate-x-1/2 hidden group-hover:block bg-slate-800 text-white text-xs p-2 rounded whitespace-nowrap z-10 shadow-lg border border-slate-700">
                Scene {scene.scene}: {scene.header}
              </div>
            </div>
          ))}
        </div>
        <div className="mt-2 text-center text-xs text-slate-400">Scene Chronology (Start to Finish)</div>
      </Card>

      {/* NEW: Framework Analysis Section */}
      <Card className="p-6">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center space-x-3">
            <Layers className="text-purple-600" size={24} />
            <div>
              <h2 className="text-xl font-bold text-slate-900">Professional Framework Analysis</h2>
              <p className="text-sm text-slate-600">Analyze your script using 4 established storytelling frameworks</p>
            </div>
          </div>
          <button
            onClick={requestFrameworks}
            disabled={isFrameworkLoading}
            className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center space-x-2"
          >
            {isFrameworkLoading ? (
              <>
                <Loader2 className="animate-spin" size={16} />
                <span>Analyzing...</span>
              </>
            ) : (
              <>
                <Sparkles size={16} />
                <span>Generate Framework Analysis</span>
              </>
            )}
          </button>
        </div>

        {frameworkError && (
          <div className="mt-4 p-4 bg-red-50 border border-red-200 rounded-lg flex items-center space-x-3">
            <AlertCircle className="text-red-500 flex-shrink-0" />
            <p className="text-red-700 text-sm">{frameworkError}</p>
          </div>
        )}

        {frameworkAnalysis && frameworkAnalysis.analyses && (
          <div className="mt-6">
            <FrameworkTabs
              analyses={frameworkAnalysis.analyses}
              onBeatSelect={() => {}}
              selectedBeat={null}
              onRewriteBeat={handleBeatRewrite}
            />

            {/* Full Rewrite Button - Shows when beat rewrites are approved */}
            {approvedBeatRewrites.length > 0 && (
              <div className="mt-8 bg-gradient-to-r from-purple-50 to-blue-50 border border-purple-200 rounded-xl p-6">
                <div className="flex items-start justify-between">
                  <div>
                    <h3 className="text-lg font-bold text-slate-900 flex items-center space-x-2 mb-2">
                      <Sparkles className="w-5 h-5 text-purple-600" />
                      <span>Ready for Full Script Rewrite</span>
                    </h3>
                    <p className="text-sm text-slate-600 mb-1">
                      You have approved {approvedBeatRewrites.length} beat rewrite{approvedBeatRewrites.length > 1 ? 's' : ''}.
                    </p>
                    <p className="text-xs text-slate-500">
                      Generate a complete screenplay rewrite using these approved changes as style examples.
                    </p>
                  </div>
                  <button
                    onClick={() => setFullRewriteModalOpen(true)}
                    className="px-6 py-3 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors flex items-center space-x-2 shadow-md"
                  >
                    <Sparkles className="w-5 h-5" />
                    <span>Generate Full Rewrite</span>
                  </button>
                </div>
              </div>
            )}
          </div>
        )}

        {!frameworkAnalysis && !isFrameworkLoading && !frameworkError && (
          <div className="mt-4 text-center py-8 text-slate-500">
            <p className="mb-2">Click "Generate Framework Analysis" to analyze your script using:</p>
            <div className="flex justify-center space-x-6 mt-4 text-sm">
              <div className="flex items-center space-x-2">
                <div className="w-3 h-3 bg-blue-500 rounded"></div>
                <span>8-Sequence Method</span>
              </div>
              <div className="flex items-center space-x-2">
                <div className="w-3 h-3 bg-green-500 rounded"></div>
                <span>Save the Cat</span>
              </div>
              <div className="flex items-center space-x-2">
                <div className="w-3 h-3 bg-purple-500 rounded"></div>
                <span>M.I.C.E. Quotient</span>
              </div>
              <div className="flex items-center space-x-2">
                <div className="w-3 h-3 bg-orange-500 rounded"></div>
                <span>McKee Structure</span>
              </div>
            </div>
          </div>
        )}
      </Card>
    </div>
  );

  const renderBreakdown = () => (
    <div className="space-y-4">
        <div className="bg-blue-50 border border-blue-200 p-4 rounded-lg flex items-start space-x-3">
            <Info className="text-blue-500 flex-shrink-0 mt-1" size={20} />
            <div>
                <h4 className="font-bold text-blue-900 text-sm">Understanding Intensity Score</h4>
                <p className="text-xs text-blue-800 mt-1">
                    The score (0-20+) estimates dramatic energy based on action verbs, punctuation, and conflict markers.
                    <br/>
                    <span className="font-semibold">0-5:</span> Low/Calm •
                    <span className="font-semibold ml-2">6-15:</span> Medium/Drama •
                    <span className="font-semibold ml-2">16+:</span> High/Action
                </p>
            </div>
        </div>

      {analysis.scenes.map((scene, idx) => (
        <div
            key={idx}
            className="bg-white border-l-4 border-l-indigo-500 shadow-sm rounded-r-lg p-4 hover:shadow-md transition-all cursor-pointer group"
            onClick={() => openSceneDetails(scene)}
        >
          <div className="flex flex-col md:flex-row md:items-center justify-between mb-3 pb-3 border-b border-slate-100">
              <div>
                  <div className="flex items-center space-x-2">
                      <span className="bg-slate-800 text-white text-xs font-bold px-2 py-1 rounded">SCENE {scene.id}</span>
                      <h4 className="font-bold text-slate-800">{scene.header}</h4>
                  </div>
                  <div className="mt-1 flex space-x-2 text-xs text-slate-500">
                      <span className="flex items-center"><MapPin size={12} className="mr-1"/> {scene.location}</span>
                      <span>•</span>
                      <span className="flex items-center">
                          {scene.isNight ? <Moon size={12} className="mr-1"/> : <Sun size={12} className="mr-1"/>}
                          {scene.type}
                      </span>
                  </div>
              </div>
              <div className="mt-2 md:mt-0 flex items-center space-x-2">
                   <Badge type={scene.intensityScore > 3 ? "danger" : "neutral"}>
                      Intensity: {scene.intensityScore}
                   </Badge>
                   <div className="text-slate-300 group-hover:text-indigo-500 transition-colors">
                       <ChevronRight size={20} />
                   </div>
              </div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                  <h5 className="text-xs font-bold text-slate-400 uppercase mb-2">Cast</h5>
                  <div className="flex flex-wrap gap-2">
                      {Array.from(scene.characters).length > 0 ? Array.from(scene.characters).map(char => (
                          <Badge key={char} type="primary">{char}</Badge>
                      )) : <span className="text-xs text-slate-400 italic">No dialogue characters detected</span>}
                  </div>
              </div>
              <div>
                  <h5 className="text-xs font-bold text-slate-400 uppercase mb-2">Potential Props/Objects</h5>
                  <div className="flex flex-wrap gap-2">
                       {analysis.props.filter(p => scene.text.join(' ').includes(p.name)).length > 0 ?
                          analysis.props.filter(p => scene.text.join(' ').includes(p.name)).map(p => (
                              <Badge key={p.name} type="warning">{p.name}</Badge>
                          ))
                       : <span className="text-xs text-slate-400 italic">None automatically detected</span>}
                  </div>
              </div>
          </div>
        </div>
      ))}
    </div>
  );

  const renderCharacters = () => {
    // Handler for when user clicks a scene in the timeline
    const handleTimelineSceneClick = (sceneNum) => {
        const scene = analysis.scenes.find(s => s.id === sceneNum);
        if (scene) {
            openSceneDetails(scene);
        }
    };

    return (
        <div className="space-y-6">
            {/* Character Timeline Visualization */}
            <CharacterTimeline
                characters={analysis.characters}
                totalScenes={analysis.totalScenes}
                onSceneClick={handleTimelineSceneClick}
            />

            {/* Existing Character Table */}
            <div className="bg-white rounded-xl shadow-sm border border-slate-200 overflow-hidden">
                <div className="p-4 bg-slate-50 border-b border-slate-200">
                    <h3 className="font-bold text-slate-800 flex items-center">
                        <Users size={18} className="mr-2 text-purple-500" />
                        Full Cast Statistics
                    </h3>
                </div>
                <table className="w-full text-left text-sm">
                    <thead className="bg-slate-50 border-b border-slate-200">
                        <tr>
                            <th className="px-6 py-4 font-semibold text-slate-700">Character</th>
                            <th className="px-6 py-4 font-semibold text-slate-700">First Scene</th>
                            <th className="px-6 py-4 font-semibold text-slate-700">Last Scene</th>
                            <th className="px-6 py-4 font-semibold text-slate-700">Scenes</th>
                            <th className="px-6 py-4 font-semibold text-slate-700">Lines</th>
                            <th className="px-6 py-4 font-semibold text-slate-700">Est. Weight</th>
                        </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-100">
                        {analysis.characters.map((char, i) => {
                            const hasGaps = char.gaps && char.gaps.length > 0;
                            const longGaps = char.gaps?.filter(g => g.size > 10) || [];

                            return (
                                <tr key={i} className={`hover:bg-slate-50 ${longGaps.length > 0 ? 'bg-amber-50/50' : ''}`}>
                                    <td className="px-6 py-4">
                                        <div className="font-medium text-slate-900">{char.name}</div>
                                        {longGaps.length > 0 && (
                                            <div className="text-xs text-amber-600 flex items-center mt-1">
                                                <AlertTriangle size={10} className="mr-1" />
                                                {longGaps.length} long gap{longGaps.length > 1 ? 's' : ''}
                                            </div>
                                        )}
                                    </td>
                                    <td className="px-6 py-4 text-slate-600">
                                        <span className="font-mono text-xs bg-amber-100 text-amber-700 px-2 py-0.5 rounded">
                                            #{char.firstAppearance}
                                        </span>
                                    </td>
                                    <td className="px-6 py-4 text-slate-600">
                                        <span className="font-mono text-xs bg-slate-100 text-slate-600 px-2 py-0.5 rounded">
                                            #{char.lastAppearance}
                                        </span>
                                    </td>
                                    <td className="px-6 py-4 text-slate-600">{char.sceneCount}</td>
                                    <td className="px-6 py-4 text-slate-600">{char.lines}</td>
                                    <td className="px-6 py-4">
                                        <div className="flex items-center">
                                            <div className="w-24 h-2 bg-slate-100 rounded-full overflow-hidden">
                                                <div
                                                    className="h-full bg-blue-500"
                                                    style={{ width: `${(char.lines / analysis.characters.reduce((acc, c) => acc + c.lines, 0)) * 100}%` }}
                                                ></div>
                                            </div>
                                        </div>
                                    </td>
                                </tr>
                            );
                        })}
                    </tbody>
                </table>
            </div>

            {/* Character Introduction Timeline */}
            <div className="bg-white rounded-xl shadow-sm border border-slate-200 overflow-hidden">
                <div className="p-4 bg-slate-50 border-b border-slate-200">
                    <h3 className="font-bold text-slate-800 flex items-center">
                        <Clock size={18} className="mr-2 text-blue-500" />
                        Introduction Order
                    </h3>
                    <p className="text-xs text-slate-500 mt-1">
                        When each character first appears in the story
                    </p>
                </div>
                <div className="p-4">
                    <div className="relative">
                        {/* Timeline bar */}
                        <div className="absolute left-4 top-0 bottom-0 w-0.5 bg-slate-200"></div>

                        {/* Character entries sorted by first appearance */}
                        <div className="space-y-3">
                            {[...analysis.characters]
                                .sort((a, b) => a.firstAppearance - b.firstAppearance)
                                .slice(0, 15)
                                .map((char, i) => {
                                    const pct = Math.round((char.firstAppearance / analysis.totalScenes) * 100);
                                    const isLate = char.firstAppearance > analysis.totalScenes * 0.5;

                                    return (
                                        <div key={i} className="flex items-center ml-4 pl-6 relative">
                                            {/* Dot on timeline */}
                                            <div className={`absolute left-0 w-3 h-3 rounded-full border-2 border-white shadow ${
                                                i === 0 ? 'bg-amber-500' :
                                                isLate ? 'bg-rose-400' :
                                                'bg-blue-400'
                                            }`}></div>

                                            {/* Content */}
                                            <div className="flex-1 flex items-center justify-between bg-slate-50 rounded-lg px-3 py-2">
                                                <div>
                                                    <span className="font-medium text-slate-800">{char.name}</span>
                                                    <span className="text-xs text-slate-400 ml-2">
                                                        Scene {char.firstAppearance} ({pct}% into story)
                                                    </span>
                                                </div>
                                                {isLate && (
                                                    <Badge type="warning">Late intro</Badge>
                                                )}
                                                {i === 0 && (
                                                    <Badge type="success">Protagonist</Badge>
                                                )}
                                            </div>
                                        </div>
                                    );
                                })}
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
  };

  const renderLocations = () => (
     <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <Card className="p-0 overflow-hidden">
            <div className="p-4 bg-slate-50 border-b border-slate-200 font-semibold text-slate-700">Interior Locations</div>
            <div className="divide-y divide-slate-100">
                {analysis.locations.filter(l => l.type === 'Interior').map((loc, i) => (
                    <div key={i} className="p-4 flex justify-between items-center hover:bg-slate-50">
                        <span className="font-medium text-slate-800">{loc.name}</span>
                        <Badge>{loc.count} Scenes</Badge>
                    </div>
                ))}
            </div>
        </Card>
        <Card className="p-0 overflow-hidden">
            <div className="p-4 bg-slate-50 border-b border-slate-200 font-semibold text-slate-700">Exterior Locations</div>
            <div className="divide-y divide-slate-100">
                {analysis.locations.filter(l => l.type === 'Exterior').map((loc, i) => (
                    <div key={i} className="p-4 flex justify-between items-center hover:bg-slate-50">
                        <span className="font-medium text-slate-800">{loc.name}</span>
                        <Badge>{loc.count} Scenes</Badge>
                    </div>
                ))}
            </div>
        </Card>
     </div>
  );

  // --- Script Editor Tab ---
  const renderEditorTab = () => {
    return (
      <div className="h-[calc(100vh-250px)]">
        <ScriptEditor
          initialText={scriptText}
          onTextChange={(newText) => setScriptText(newText)}
          onAnalyze={handleAnalyze}
          isAnalyzing={isAnalyzing}
        />
      </div>
    );
  };

  // --- The Professor Tab (Merged with AI Insights) ---
  const renderFeedbackTab = () => {
    const severityStyles = {
        critical: "bg-red-50 border-red-200 text-red-800",
        warning: "bg-amber-50 border-amber-200 text-amber-800",
        suggestion: "bg-blue-50 border-blue-200 text-blue-800",
        praise: "bg-green-50 border-green-200 text-green-800"
    };

    const severityIcons = {
        critical: <AlertOctagon size={16} className="text-red-500" />,
        warning: <AlertTriangle size={16} className="text-amber-500" />,
        suggestion: <Sparkles size={16} className="text-blue-500" />,
        praise: <CheckCircle size={16} className="text-green-500" />
    };

    return (
      <div className="space-y-6">
          {/* Header */}
          <div className="bg-slate-900 text-white p-6 rounded-xl shadow-lg">
              <div className="flex items-center space-x-4">
                  <div className="bg-white/10 p-3 rounded-full">
                      <GraduationCap size={32} className="text-white" />
                  </div>
                  <div>
                      <h2 className="text-xl font-bold">The Professor's Office</h2>
                      <p className="text-slate-300 text-sm">Expert screenplay analysis powered by Claude AI</p>
                  </div>
              </div>
          </div>

          {/* Quick Observations - Always visible first */}
          {analysis.professorFeedback.length > 0 && (
              <div className="bg-slate-50 border border-slate-200 rounded-xl p-6">
                  <h3 className="text-lg font-bold text-slate-800 mb-4">Quick Observations</h3>
                  <div className="space-y-3">
                      {analysis.professorFeedback.map((item, i) => (
                          <ProfessorBubble
                              key={i}
                              name="The Professor"
                              role="Story Structure Expert"
                              text={item.text}
                              type={item.type || "critique"}
                              fix={item.fix}
                          />
                      ))}
                  </div>
              </div>
          )}

          {/* Loading State */}
          {isAiLoading && (
              <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-12 text-center">
                  <Loader2 className="w-12 h-12 text-slate-700 animate-spin mx-auto mb-4" />
                  <h3 className="text-lg font-semibold text-slate-700 mb-2">The Professor is reviewing your script...</h3>
                  <p className="text-sm text-slate-500">Analyzing structure, characters, and pacing</p>
              </div>
          )}

          {/* Error State */}
          {aiError && (
              <div className="bg-red-50 border border-red-200 rounded-xl p-6 flex items-start space-x-4">
                  <AlertCircle className="text-red-500 flex-shrink-0" size={24} />
                  <div>
                      <h3 className="font-bold text-red-800">Analysis Failed</h3>
                      <p className="text-sm text-red-700 mt-1">{aiError}</p>
                      <button
                          onClick={requestAiAnalysis}
                          className="mt-3 px-4 py-2 bg-red-600 text-white text-sm font-medium rounded-lg hover:bg-red-700 transition-colors"
                      >
                          Try Again
                      </button>
                  </div>
              </div>
          )}

          {/* Professor's Analysis Results */}
          {aiAnalysis && !isAiLoading && (
              <div className="space-y-6">
                  {/* Grade & Headline */}
                  <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-6">
                      <div className="flex items-start justify-between mb-4">
                          <div className="flex-1">
                              <h3 className="text-2xl font-bold text-slate-800 mb-2">{aiAnalysis.headline}</h3>
                              <div className="flex flex-wrap items-center gap-4 mt-2">
                                  <span className="text-sm text-slate-500">Genre: <span className="font-medium text-slate-700">{aiAnalysis.genreGuess}</span></span>
                                  <span className="text-sm text-slate-500">Commercial: <span className="font-medium text-slate-700">{aiAnalysis.commercialPotential}</span></span>
                              </div>
                          </div>
                          <div className={`text-4xl font-bold px-4 py-2 rounded-lg flex-shrink-0 ${
                              aiAnalysis.overallGrade.startsWith('A') ? 'bg-green-100 text-green-700' :
                              aiAnalysis.overallGrade.startsWith('B') ? 'bg-blue-100 text-blue-700' :
                              aiAnalysis.overallGrade.startsWith('C') ? 'bg-amber-100 text-amber-700' :
                              'bg-red-100 text-red-700'
                          }`}>
                              {aiAnalysis.overallGrade}
                          </div>
                      </div>
                      {aiAnalysis.comparables && aiAnalysis.comparables.length > 0 && (
                          <div className="mt-4 pt-4 border-t border-slate-100">
                              <span className="text-xs font-bold text-slate-400 uppercase">Comparable Films</span>
                              <div className="flex flex-wrap gap-2 mt-2">
                                  {aiAnalysis.comparables.map((comp, i) => (
                                      <Badge key={i} type="purple">{comp}</Badge>
                                  ))}
                              </div>
                          </div>
                      )}
                  </div>

                  {/* Professor's Insights */}
                  <div className="space-y-4">
                      {aiAnalysis.insights.map((insight, idx) => (
                          <div key={idx} className={`rounded-xl border p-5 ${severityStyles[insight.severity]}`}>
                              <div className="flex items-start space-x-3">
                                  <div className="mt-0.5">{severityIcons[insight.severity]}</div>
                                  <div className="flex-1">
                                      <div className="flex items-center space-x-2 mb-1">
                                          <h4 className="font-bold">{insight.title}</h4>
                                          <Badge type="neutral">{insight.category}</Badge>
                                      </div>
                                      <p className="text-sm mb-2 whitespace-pre-wrap">{insight.finding}</p>
                                      {insight.recommendation && (
                                          <div className="text-sm font-medium flex items-start space-x-1 mt-2 pt-2 border-t border-current/10">
                                              <span className="opacity-60">→</span>
                                              <span>{insight.recommendation}</span>
                                          </div>
                                      )}
                                  </div>
                              </div>
                          </div>
                      ))}
                  </div>

                  {/* Follow-up Questions Section */}
                  <div className="bg-slate-50 border border-slate-200 rounded-xl p-6">
                      <h3 className="text-lg font-bold text-slate-800 mb-4 flex items-center">
                          <MessageCircle size={20} className="mr-2" />
                          Ask The Professor a Follow-up Question
                      </h3>

                      {/* Conversation History */}
                      {professorConversation.length > 0 && (
                          <div className="space-y-3 mb-4 max-h-96 overflow-y-auto">
                              {professorConversation.map((msg, idx) => (
                                  <div key={idx} className={`p-3 rounded-lg ${
                                      msg.role === 'user'
                                          ? 'bg-blue-50 border border-blue-200 ml-8'
                                          : 'bg-white border border-slate-200 mr-8'
                                  }`}>
                                      <div className="text-xs font-bold text-slate-500 mb-1">
                                          {msg.role === 'user' ? 'You' : 'The Professor'}
                                      </div>
                                      <div className="text-sm text-slate-800 whitespace-pre-wrap">{msg.content}</div>
                                  </div>
                              ))}
                          </div>
                      )}

                      {/* Question Input */}
                      <div className="flex space-x-2">
                          <input
                              type="text"
                              value={followUpQuestion}
                              onChange={(e) => setFollowUpQuestion(e.target.value)}
                              onKeyPress={(e) => e.key === 'Enter' && askFollowUpQuestion()}
                              placeholder="e.g., What are the other problematic scenes you mentioned?"
                              className="flex-1 px-4 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-slate-500"
                              disabled={isAskingFollowUp}
                          />
                          <button
                              onClick={askFollowUpQuestion}
                              disabled={isAskingFollowUp || !followUpQuestion.trim()}
                              className="px-6 py-2 bg-slate-800 text-white font-medium rounded-lg hover:bg-slate-900 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center"
                          >
                              {isAskingFollowUp ? (
                                  <Loader2 size={18} className="animate-spin" />
                              ) : (
                                  'Ask'
                              )}
                          </button>
                      </div>
                      <p className="text-xs text-slate-500 mt-2">
                          Ask for clarification, more details, or specific scene recommendations
                      </p>
                  </div>
              </div>
          )}

          {/* Request Professor Analysis - Show when no AI analysis yet */}
          {!aiAnalysis && !isAiLoading && !aiError && (
              <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-12 text-center">
                  <GraduationCap className="w-16 h-16 text-slate-300 mx-auto mb-4" />
                  <h3 className="text-lg font-semibold text-slate-700 mb-2">Ready for The Professor's Analysis</h3>
                  <p className="text-sm text-slate-500 mb-6 max-w-md mx-auto">
                      Get comprehensive feedback on your script's structure, character arcs, pacing, and commercial potential.
                      The Professor will identify ALL problematic scenes and provide actionable recommendations.
                  </p>
                  <button
                      onClick={requestAiAnalysis}
                      className="px-6 py-3 bg-gradient-to-r from-slate-800 to-slate-900 text-white font-semibold rounded-lg hover:from-slate-900 hover:to-black transition-all shadow-md hover:shadow-lg inline-flex items-center"
                  >
                      <GraduationCap size={20} className="mr-2" />
                      Request Professor Analysis
                  </button>
              </div>
          )}
      </div>
    );
  };

  // --- RESTORED: Story Architecture Analyzer Tab ---
  const renderStoryAnalyzer = () => {
    return (
        <div className="space-y-6">
            <div className="flex justify-between items-center bg-white p-2 rounded-lg shadow-sm border border-slate-200">
                <div className="flex space-x-2">
                    <button
                        onClick={() => setFramework('8-3')}
                        className={`px-4 py-2 rounded-md text-sm font-bold transition-colors ${framework === '8-3' ? 'bg-indigo-600 text-white' : 'text-slate-600 hover:bg-slate-100'}`}
                    >
                        8-3 Framework
                    </button>
                    <button
                        onClick={() => setFramework('stc')}
                        className={`px-4 py-2 rounded-md text-sm font-bold transition-colors ${framework === 'stc' ? 'bg-amber-500 text-white' : 'text-slate-600 hover:bg-slate-100'}`}
                    >
                        Save The Cat!
                    </button>
                </div>
                <div className="text-xs text-slate-500 font-medium px-2">
                    Total Script Pages: {analysis.totalPages}
                </div>
            </div>

            {framework === 'stc' && (
                <div className="space-y-6">
                    {/* Snyder Persona Feedback Header */}
                    <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 flex items-start space-x-4">
                        <div className="bg-amber-500 text-white p-2 rounded-full flex-shrink-0">
                            <Zap size={20} />
                        </div>
                        <div>
                             <h4 className="font-bold text-amber-900">The Cat Saver's Take</h4>
                             <p className="text-sm text-amber-800 mb-2">"Let's check those beats! Are we hitting the page counts? Is it primal?"</p>
                             <div className="space-y-2 mt-3">
                                {analysis.snyderFeedback && analysis.snyderFeedback.length > 0 ? (
                                    analysis.snyderFeedback.map((feedback, idx) => (
                                        <div key={idx} className={`flex items-start text-sm p-2 rounded ${feedback.status === 'ok' ? 'bg-green-50 text-green-800' : 'bg-amber-100 text-amber-900'}`}>
                                            {feedback.status === 'ok' ? <CheckCircle size={14} className="mr-2 mt-0.5 flex-shrink-0"/> : <AlertTriangle size={14} className="mr-2 mt-0.5 flex-shrink-0"/>}
                                            <div>
                                                <span className="font-semibold">{feedback.beat}:</span> {feedback.text}
                                                {feedback.fix && <p className="text-xs mt-1 opacity-75">→ {feedback.fix}</p>}
                                            </div>
                                        </div>
                                    ))
                                ) : (
                                    <div className="text-sm text-amber-700 italic">
                                        Feedback will appear here after Snyder analysis is implemented.
                                    </div>
                                )}
                             </div>
                        </div>
                    </div>

                    <div className="bg-white rounded-xl shadow-sm border border-slate-200 overflow-hidden">
                        <div className="p-6 border-b border-slate-100">
                            <h3 className="text-lg font-bold text-slate-800 flex items-center">
                                <Zap className="mr-2 text-amber-500" /> Blake Snyder's Beat Sheet
                            </h3>
                            <p className="text-sm text-slate-500">Comparing ideal structural beats against your script's current pacing.</p>
                        </div>
                        <div className="divide-y divide-slate-100">
                            {analysis.stcAnalysis.map((beat, i) => (
                                <div
                                    key={i}
                                    className="p-4 grid grid-cols-12 gap-4 hover:bg-slate-50 transition-colors cursor-pointer"
                                    onClick={() => beat.scene && openSceneDetails(beat.scene, `${beat.name} Beat: ${beat.scene.header}`)}
                                >
                                    <div className="col-span-3">
                                        <div className="font-bold text-slate-800">{beat.name}</div>
                                        <div className="text-xs text-slate-500">{beat.description}</div>
                                    </div>
                                    <div className="col-span-2 flex flex-col justify-center">
                                        <span className="text-xs font-semibold text-slate-400 uppercase">Ideal Page</span>
                                        <span className="text-xl font-mono font-bold text-amber-500">{beat.targetPage}</span>
                                    </div>
                                    <div className="col-span-7 border-l border-slate-200 pl-4">
                                        <span className="text-xs font-semibold text-slate-400 uppercase mb-1 block">Nearest Scene Match</span>
                                        {beat.scene ? (
                                            <div className="bg-slate-50 p-2 rounded border border-slate-100">
                                                <div className="flex justify-between items-start">
                                                    <span className="font-bold text-sm text-slate-700">{beat.scene.header}</span>
                                                    <Badge type="neutral">Pg ~{Math.round(beat.scene.pageEstimate)}</Badge>
                                                </div>
                                                <p className="text-xs text-slate-500 mt-1 line-clamp-2">
                                                    {beat.scene.text.slice(0, 2).join(' ')}...
                                                </p>
                                            </div>
                                        ) : (
                                            <div className="text-xs text-red-400 italic">No scene found in this range. Script might be too short.</div>
                                        )}
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>
            )}

            {framework === '8-3' && (
                <div className="space-y-4">
                     <div className="bg-indigo-900 text-white p-6 rounded-xl shadow-md">
                        <div className="flex items-start justify-between">
                            <div>
                                <h3 className="text-lg font-bold flex items-center mb-2">
                                    <Layers className="mr-2" /> The 8-3 Framework
                                </h3>
                                <p className="text-indigo-200 text-sm max-w-2xl">
                                    Macro Sequences (The Skeleton) + A-R-D Micro-beats.
                                </p>
                            </div>
                            <div className="bg-indigo-800 p-3 rounded-lg max-w-xs text-xs text-indigo-100 italic border border-indigo-700">
                                "The Architect says: Ensure every sequence has a Driver (Action), a Shift (Reaction), and Fallout (Dust)."
                            </div>
                        </div>
                    </div>

                    {analysis.sequences83.map((seq, i) => (
                        <div key={seq.id} className="bg-white rounded-xl shadow-sm border border-slate-200 overflow-hidden">
                            <div className="bg-slate-50 p-4 border-b border-slate-200 flex justify-between items-center">
                                <div>
                                    <h4 className="font-bold text-indigo-900">Sequence {seq.id}</h4>
                                    <p className="text-xs text-slate-500">Pages {seq.range} • {seq.scenes.length} Scenes</p>
                                </div>
                                <div className="flex space-x-2">
                                     <div className={`w-3 h-3 rounded-full ${seq.ard.action ? 'bg-green-500' : 'bg-red-300'}`} title="Action Detected?"></div>
                                     <div className={`w-3 h-3 rounded-full ${seq.ard.reaction ? 'bg-green-500' : 'bg-red-300'}`} title="Reaction Detected?"></div>
                                     <div className={`w-3 h-3 rounded-full ${seq.ard.dust ? 'bg-green-500' : 'bg-red-300'}`} title="Dust Detected?"></div>
                                </div>
                            </div>

                            <div className="p-4 grid grid-cols-1 md:grid-cols-3 gap-4">
                                {/* Action Column */}
                                <div
                                    className="bg-green-50 rounded-lg p-3 border border-green-100 cursor-pointer hover:border-green-300 transition-colors"
                                    onClick={() => seq.ard.action && openSceneDetails(seq.ard.action, `Seq ${seq.id} ACTION: ${seq.ard.action.header}`)}
                                >
                                    <div className="flex items-center text-green-800 font-bold text-sm mb-2">
                                        <Target size={14} className="mr-1"/> ACTION (The Driver)
                                    </div>
                                    {seq.ard.action ? (
                                        <>
                                            <div className="text-xs font-bold text-slate-700 mb-1">{seq.ard.action.header}</div>
                                            <p className="text-xs text-slate-500 line-clamp-3 italic">"{seq.ard.action.text[0]}"</p>
                                        </>
                                    ) : <span className="text-xs text-slate-400 italic">No clear start scene.</span>}
                                </div>

                                {/* Reaction Column */}
                                <div
                                    className="bg-blue-50 rounded-lg p-3 border border-blue-100 cursor-pointer hover:border-blue-300 transition-colors"
                                    onClick={() => seq.ard.reaction && openSceneDetails(seq.ard.reaction, `Seq ${seq.id} REACTION: ${seq.ard.reaction.header}`)}
                                >
                                    <div className="flex items-center text-blue-800 font-bold text-sm mb-2">
                                        <Users size={14} className="mr-1"/> REACTION (The Shift)
                                    </div>
                                     {seq.ard.reaction ? (
                                        <>
                                            <div className="text-xs font-bold text-slate-700 mb-1">{seq.ard.reaction.header}</div>
                                            <p className="text-xs text-slate-500 line-clamp-3 italic">
                                                 (Dialogue Heavy) "...{seq.ard.reaction.text.find(t => t.length > 20)?.slice(0, 40)}..."
                                            </p>
                                        </>
                                    ) : <span className="text-xs text-slate-400 italic">No clear reaction beat.</span>}
                                </div>

                                {/* Dust Column */}
                                <div
                                    className="bg-orange-50 rounded-lg p-3 border border-orange-100 cursor-pointer hover:border-orange-300 transition-colors"
                                    onClick={() => seq.ard.dust && openSceneDetails(seq.ard.dust, `Seq ${seq.id} DUST: ${seq.ard.dust.header}`)}
                                >
                                    <div className="flex items-center text-orange-800 font-bold text-sm mb-2">
                                        <Activity size={14} className="mr-1"/> DUST (The Fallout)
                                    </div>
                                    {seq.ard.dust ? (
                                        <>
                                            <div className="text-xs font-bold text-slate-700 mb-1">{seq.ard.dust.header}</div>
                                            <p className="text-xs text-slate-500 line-clamp-3 italic">
                                                (End of Sequence) "{seq.ard.dust.text[seq.ard.dust.text.length-1]}"
                                            </p>
                                        </>
                                    ) : <span className="text-xs text-slate-400 italic">Sequence appears incomplete.</span>}
                                </div>
                            </div>
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
  };

  return (
    <div className="min-h-screen bg-slate-100 text-slate-900 font-sans">
      <header className="bg-slate-900 text-white p-4 shadow-lg sticky top-0 z-50">
        <div className="max-w-6xl mx-auto flex justify-between items-center">
          <div className="flex items-center space-x-2">
            <div className="bg-rose-500 p-1.5 rounded text-white"><Box size={20} /></div>
            <h1 className="text-xl font-bold tracking-tight">Director's Lens <span className="text-slate-500 text-sm font-normal">v1.2</span></h1>
          </div>
          <div className="flex space-x-2">
            {analysis && !aiAnalysis && (
                <button
                    onClick={() => { setActiveTab('feedback'); requestAiAnalysis(); }}
                    disabled={isAiLoading}
                    className="bg-gradient-to-r from-purple-500 to-indigo-500 hover:from-purple-600 hover:to-indigo-600 text-white px-4 py-2 rounded-lg text-sm font-semibold transition-all flex items-center disabled:opacity-50 shadow-sm"
                >
                    {isAiLoading ? (
                        <><Loader2 className="mr-2 animate-spin" size={16} /> The Professor is Reading...</>
                    ) : (
                        <><GraduationCap size={16} className="mr-2" /> Professor Analysis</>
                    )}
                </button>
            )}
            {analysis && isJspdfLoaded && (
                <button
                    onClick={generatePDF}
                    className="bg-indigo-600 hover:bg-indigo-700 text-white px-4 py-2 rounded-lg text-sm font-semibold transition-colors flex items-center"
                >
                    <Download size={16} className="mr-2" /> Export Report
                </button>
            )}
            <button
                onClick={() => handleAnalyze()}
                disabled={isAnalyzing || isPdfLoading}
                className="bg-rose-500 hover:bg-rose-600 text-white px-4 py-2 rounded-lg text-sm font-semibold transition-colors flex items-center disabled:opacity-50 disabled:cursor-not-allowed"
            >
                {isAnalyzing ? (<><Loader2 className="mr-2 animate-spin" size={16} /> Processing...</>) : 'Analyze Script'}
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-6xl mx-auto p-4 md:p-6 space-y-6">
        {/* Details Modal */}
        <DetailsModal
            isOpen={modalOpen}
            onClose={() => setModalOpen(false)}
            title={modalData.title}
            content={modalData.content}
            feedback={modalData.feedback}
        />

        <div className="bg-white rounded-xl shadow-sm border border-slate-200 overflow-hidden">
          <details className="group" open>
            <summary className="flex justify-between items-center p-4 cursor-pointer bg-slate-50 hover:bg-slate-100 transition-colors list-none">
              <span className="font-semibold text-slate-700 flex items-center"><FileText size={18} className="mr-2" /> Script Source</span>
              <ChevronDown className="group-open:rotate-180 transition-transform text-slate-500" />
            </summary>
            <div className="p-4 border-t border-slate-200 space-y-4">
              <div className="flex items-center justify-center w-full">
                <label className="flex flex-col items-center justify-center w-full h-32 border-2 border-slate-300 border-dashed rounded-lg cursor-pointer bg-slate-50 hover:bg-slate-100 transition-all">
                  <div className="flex flex-col items-center justify-center pt-5 pb-6">
                    {isPdfLoading ? (
                        <>
                            <Loader2 className="w-8 h-8 mb-3 text-rose-500 animate-spin" />
                            <p className="mb-2 text-sm text-slate-500 font-semibold">Extracting text from PDF...</p>
                        </>
                    ) : (
                        <>
                            <Upload className="w-8 h-8 mb-3 text-slate-400" />
                            <p className="mb-2 text-sm text-slate-500"><span className="font-semibold">Click to upload PDF</span></p>
                            <p className="text-xs text-slate-500">Industry Standard Format (.pdf)</p>
                        </>
                    )}
                  </div>
                  <input ref={fileInputRef} type="file" className="hidden" accept="application/pdf" onChange={handleFileUpload} disabled={isPdfLoading} />
                </label>
              </div>

              {pdfError && (
                  <div className="p-3 bg-red-50 text-red-700 rounded flex items-center text-sm border border-red-200">
                    <AlertCircle size={16} className="mr-2 flex-shrink-0" />
                    {pdfError}
                  </div>
              )}

              {scriptText && (
                  <div className="mt-2">
                    <div className="flex items-center space-x-3 mb-2">
                      <button
                          onClick={() => setShowRawText(!showRawText)}
                          className="text-xs flex items-center text-slate-500 hover:text-indigo-600 font-medium"
                      >
                          {showRawText ? <EyeOff size={14} className="mr-1" /> : <Eye size={14} className="mr-1" />}
                          {showRawText ? "Hide Raw Text" : "View Extracted Text (Debug)"}
                      </button>
                      <button
                          onClick={() => setActiveTab('editor')}
                          className="text-xs flex items-center bg-indigo-600 text-white px-3 py-1.5 rounded-md hover:bg-indigo-700 transition-colors font-medium"
                      >
                          <FileEdit size={14} className="mr-1" />
                          Edit Script
                      </button>
                    </div>
                    {showRawText && (
                        <div className="p-3 bg-slate-800 text-green-400 font-mono text-xs rounded-md h-48 overflow-y-auto whitespace-pre-wrap">
                            {scriptText.slice(0, 5000) + (scriptText.length > 5000 ? "\n... (truncated)" : "")}
                        </div>
                    )}
                  </div>
              )}

              <div>
                  <div className="flex items-center justify-between mb-2">
                    <label className="text-xs font-bold text-slate-400 uppercase">Or Paste Text Directly</label>
                  </div>
                  <textarea
                    className="w-full h-48 p-4 font-mono text-sm border border-slate-300 rounded-lg focus:ring-2 focus:ring-rose-500 focus:border-rose-500 outline-none"
                    value={scriptText}
                    onChange={handleTextChange}
                    placeholder="Paste your script here using standard formatting..."
                  ></textarea>
              </div>
            </div>
          </details>
        </div>

        {analysis && (
          <div className="space-y-6">
            <div className="flex space-x-1 bg-slate-200 p-1 rounded-lg w-full md:w-auto inline-flex overflow-x-auto">
              {[
                { id: 'dashboard', label: 'Overview', icon: BarChart2 },
                { id: 'editor', label: 'Script Editor', icon: FileEdit },
                { id: 'feedback', label: 'The Professor', icon: GraduationCap },
                { id: 'story', label: 'Story Architecture', icon: BookOpen },
                { id: 'breakdown', label: 'Breakdown', icon: FileText },
                { id: 'characters', label: 'Cast Stats', icon: Users },
                { id: 'locations', label: 'Locations', icon: MapPin },
              ].map(tab => (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`flex items-center px-4 py-2 rounded-md text-sm font-medium transition-all ${
                    activeTab === tab.id
                      ? 'bg-white text-slate-900 shadow-sm'
                      : 'text-slate-600 hover:text-slate-900 hover:bg-slate-300/50'
                  }`}
                >
                  <tab.icon size={16} className="mr-2" />
                  {tab.label}
                </button>
              ))}
            </div>

            <div className="min-h-[400px]">
              {activeTab === 'dashboard' && renderDashboard()}
              {activeTab === 'editor' && renderEditorTab()}
              {activeTab === 'feedback' && renderFeedbackTab()}
              {activeTab === 'story' && renderStoryAnalyzer()}
              {activeTab === 'breakdown' && renderBreakdown()}
              {activeTab === 'characters' && renderCharacters()}
              {activeTab === 'locations' && renderLocations()}
            </div>
          </div>
        )}
      </main>

      {/* Rewrite Modals */}
      {rewriteModalOpen && (
        <RewriteModal
          beat={currentBeatRewrite?.beat}
          rewriteData={beatRewriteData}
          isLoading={isRewriteLoading}
          error={rewriteError}
          onAccept={handleAcceptBeatRewrite}
          onReject={handleRejectBeatRewrite}
          onClose={handleRejectBeatRewrite}
        />
      )}

      {fullRewriteModalOpen && (
        <FullRewriteModal
          scriptText={scriptText}
          analysis={analysis}
          frameworkAnalyses={frameworkAnalysis}
          approvedBeatRewrites={approvedBeatRewrites}
          isLoading={isRewriteLoading}
          error={rewriteError}
          rewriteResult={fullRewriteResult}
          onGenerate={handleGenerateFullRewrite}
          onAccept={handleAcceptFullRewrite}
          onClose={() => {
            setFullRewriteModalOpen(false);
            setFullRewriteResult(null);
            setRewriteError(null);
          }}
        />
      )}
    </div>
  );
}
